import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Play, Pause, Radio } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { serverTimestamp } from 'firebase/firestore';
import FirestorePersistentStorageService from '../testing/services/FirestorePersistentStorageService';
import { SecureAPI } from '../config.js';

const VOICE_CONFIG = {
  guide: '33045fd9-8010-43f6-b6b0-da3fbf326c29',
  therapist: '41745808-6790-4be4-83db-363e20db7e71'
};

const INTRO_MUSIC_OPTIONS = [
  '/music/intro-outro-8.mp3', // Upbeat
  '/music/intro-outro-7.mp3',
  '/music/intro-outro-19.mp3',
  '/music/intro-outro-7.mp3',
  '/music/intro-outro-13.mp3'
];

const INTERMISSION_MUSIC_OPTIONS = [
    '/music/intro-outro-21.mp3',
  '/music/intro-outro-20.mp3', // Ambient
  '/music/intro-outro-6.mp3',
  '/music/intro-outro-17.mp3',
  '/music/intro-outro-18.mp3',
  '/music/intro-outro-16.mp3'
];
const COMPANY_LOGOS = {
  TheMontclarion: {
    name: 'Montclair State University',
    color: '#C41E3A', // MSU's official red
    logos: [
      'https://themontclarion.org/wp-content/uploads/2023/11/logo-2.png',
      'https://www.montclair.edu/wp-content/uploads/2024/01/msu-logo.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23C41E3A"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%23C41E3A">M</text></svg>'
    ]
  },
  TSLA: {
    name: 'Tesla',
    color: '#E82127', // Tesla red
    logos: [
      'https://www.tesla.com/favicon.ico',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e8/Tesla_logo.png/1200px-Tesla_logo.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23E82127"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%23E82127">T</text></svg>'
    ]
  },
  AAPL: {
    name: 'Apple',
    color: '#555555',
    logos: [
      'https://www.apple.com/favicon.ico',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Apple_logo_black.svg/1200px-Apple_logo_black.svg.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23555555"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%23555555">A</text></svg>'
    ]
  },
  GOOGL: {
    name: 'Google',
    color: '#4285F4',
    logos: [
            'https://www.google.com/favicon.ico',
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSWIl8zC8WAMHi5JVmKUb3YVvZd5gvoCdy-NQ&s',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/1200px-Google_2015_logo.svg.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%234285F4"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%234285F4">G</text></svg>'
    ]
  },
  MSFT: {
    name: 'Microsoft',
    color: '#00A4EF',
    logos: [
      'https://www.microsoft.com/favicon.ico',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Microsoft_logo.svg/1200px-Microsoft_logo.svg.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2300A4EF"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%2300A4EF">M</text></svg>'
    ]
  },
  AMZN: {
    name: 'Amazon',
    color: '#FF9900',
    logos: [
      'https://www.amazon.com/favicon.ico',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/1200px-Amazon_logo.svg.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23FF9900"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%23FF9900">A</text></svg>'
    ]
  },
  META: {
    name: 'Meta',
    color: '#0A66C2',
    logos: [
      'https://pngimg.com/d/meta_PNG5.png',
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTPmZ3_iTMphNtx4ry_MTT-ElUl1SCjO-jZCg&s',
      'https://www.citypng.com/public/uploads/preview/facebook-meta-logo-icon-hd-png-701751694777703xqxtpvbu9q.png?v=2025082307'
    ]
  },
  NVDA: {
    name: 'NVIDIA',
    color: '#76B900',
    logos: [
      'https://www.nvidia.com/favicon.ico',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/NVIDIA_logo.svg/1200px-NVIDIA_logo.svg.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2376B900"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%2376B900">N</text></svg>'
    ]
  },
  NFLX: {
    name: 'Netflix',
    color: '#E50914',
    logos: [
      'https://www.netflix.com/favicon.ico',
      'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6d/Netflix_logo.svg/1200px-Netflix_logo.svg.png',
      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23E50914"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="bold" fill="%23E50914">N</text></svg>'
    ]
  },
    OPENAI: {
    name: 'OpenAI',
    color: '#000000',
    logos: [
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTBdcgkdcDy5z4PYGx_kDJB1AvvC_x1pCBbbQ&s',
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS2LmgGUNPfLs9NmqfycQ0lLiBFplJXOi70EA&s',
      'https://avatars.githubusercontent.com/u/14957082'
    ]
  },
  XAI: {
    name: 'xAI (Grok)',
    color: '#000000',
    logos: [
      'https://pbs.twimg.com/profile_images/1683325380441128960/yRsRRjGO_400x400.jpg',
      'https://avatars.githubusercontent.com/u/132372032',
      'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png'
    ]
  }
};
const CompanyLogo = ({ ticker, name, size = '40px', showInitial = true }) => {
  const [logoIndex, setLogoIndex] = useState(0);
  const [showInitial_state, setShowInitialState] = useState(false);
  
  const config = COMPANY_LOGOS[ticker];
  
  if (!config) {
    return (
      <div style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${parseInt(size) * 0.6}px`,
        fontWeight: '700',
        color: '#6b7280',
        border: '2px solid #e5e7eb'
      }}>
        {ticker.charAt(0)}
      </div>
    );
  }

  const handleImageError = () => {
    if (logoIndex < config.logos.length - 1) {
      setLogoIndex(logoIndex + 1);
    } else {
      setShowInitialState(true);
    }
  };

  if (showInitial_state && showInitial) {
    return (
      <div style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: config.color + '15',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${parseInt(size) * 0.6}px`,
        fontWeight: '700',
        color: config.color,
        border: `2px solid ${config.color}40`,
        flexShrink: 0
      }}>
        {ticker.charAt(0)}
      </div>
    );
  }

  return (
    <img
      src={config.logos[logoIndex]}
      alt={ticker}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'contain',
        background: '#f9fafb',
        border: '2px solid #e5e7eb',
        padding: '4px',
        flexShrink: 0
      }}
      onError={handleImageError}
      crossOrigin="anonymous"
    />
  );
};

const CompaniesLiveStation = ({ company, userName, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const isManualNavigationRef = useRef(false); // ✅ Track manual card navigation
const hasTransitionedToCard = useRef(new Set()); // Track which cards we've manually transitioned to
const isPlayingTransition = useRef(false); // Track if currently playing transition
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [transcript, setTranscript] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasListenedToday, setHasListenedToday] = useState(false);
  const [showFarewell, setShowFarewell] = useState(false);
  const [segments, setSegments] = useState([]);
  const abortControllerRef = useRef(new AbortController());
  const shouldStopRef = useRef(false);
const isPausedRef = useRef(false);
  const audioRef = useRef(null);
  const currentAudioRef = useRef(null);
  const audioQueueRef = useRef(new Map());
  const isPreloadingRef = useRef(new Set());
  const playbackSpeedRef = useRef(playbackSpeed);
const [isPaused, setIsPaused] = useState(false); 
const [currentSegment, setCurrentSegment] = useState(0);
const segmentsRef = useRef([]);
const [preGeneratedSources, setPreGeneratedSources] = useState(new Set());
const preGeneratedRef = useRef(new Set());
const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
const [totalNewsCount, setTotalNewsCount] = useState(0);
const scriptLinesRef = useRef([]);
let lastUsedIntroMusic = null;
let lastUsedIntermissionMusic = null;
const [autoScrollTranscript, setAutoScrollTranscript] = useState(true);
const transcriptEndRef = useRef(null);
const newsSegmentsRef = useRef([]); // Track which lines belong to which news item
const [newsSources, setNewsSources] = useState([]);
const storageService = new FirestorePersistentStorageService();
const [loadingMessage, setLoadingMessage] = useState('Searching...');
const [newsCards, setNewsCards] = useState([]);
const [currentCardIndex, setCurrentCardIndex] = useState(0);
const [isLoadingCards, setIsLoadingCards] = useState(true);
const newsCardsRef = useRef(null);
const [isSearchingComplete, setIsSearchingComplete] = useState(false);
const [showArticleConfirm, setShowArticleConfirm] = useState(false);
const [selectedArticle, setSelectedArticle] = useState(null);
const [hasGuest, setHasGuest] = useState(false);
const [guestName, setGuestName] = useState('');
const activeAudioElements = useRef(new Set()); // ✅ Track ALL audio elements
const preloadAbortControllers = useRef(new Map()); // ✅ Separate controllers for preloads
const isNavigatingRef = useRef(false); // ✅ Track navigation state
  const intermissionAudioRef = useRef(null);
  // ✅ Add geo-location state at the very top
  const [userLocation, setUserLocation] = useState(null);
  const [isInMontclair, setIsInMontclair] = useState(false);
const [isNavigating, setIsNavigating] = useState(false);
const [scriptToCardMap, setScriptToCardMap] = useState({});
const currentLineIndexRef = useRef(0);
const lastNavigationTimeRef = useRef(0);
const NAVIGATION_COOLDOWN = 10000; // 10 seconds
  // ✅ Check user's location on component mount
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          
          // Montclair, NJ bounds
          const montclairBounds = {
            north: 40.8500,
            south: 40.8000,
            east: -74.1900,
            west: -74.2200
          };
          
          const inMontclair = 
            latitude >= montclairBounds.south && 
            latitude <= montclairBounds.north && 
            longitude >= montclairBounds.west && 
            longitude <= montclairBounds.east;
          
          setIsInMontclair(inMontclair);
          
          // ✅ Console logs for debugging
          console.log('📍 User Location:', { latitude, longitude });
          console.log('🏠 Montclair Bounds:', montclairBounds);
          console.log(`${inMontclair ? '✅' : '❌'} User is ${inMontclair ? 'IN' : 'NOT in'} Montclair, NJ`);
          
          if (inMontclair) {
            console.log('🎉 Montclair user detected! Local station would be available.');
          } else {
            console.log('🌍 User is outside Montclair - showing standard stations only');
          }
        },
        (error) => {
          console.warn('⚠️ Geo-location error:', error.message);
          setIsInMontclair(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 600000 // Cache for 10 minutes
        }
      );
    } else {
      console.warn('⚠️ Geolocation not supported in this browser');
      setIsInMontclair(false);
    }
  }, []);


// ============================================
// 1️⃣ NUCLEAR CLEANUP - STOP ALL AUDIO
// ============================================
const nuclearCleanupAudio = async () => {
  log(`🔴 NUCLEAR CLEANUP - Stopping ${activeAudioElements.current.size} audio elements`);
  
  // ✅ ABORT ALL PRELOAD REQUESTS (if using preloads)
  if (preloadAbortControllers.current) {
    preloadAbortControllers.current.forEach((controller, key) => {
      try {
        controller.abort();
        log(`🛑 Aborted preload: ${key}`);
      } catch (e) {}
    });
    preloadAbortControllers.current.clear();
  }
  
  // ✅ STOP ALL TRACKED AUDIO ELEMENTS
  activeAudioElements.current.forEach(audio => {
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
    } catch (e) {}
  });
  activeAudioElements.current.clear();
  
  // ✅ Clear main refs
  currentAudioRef.current = null;
  
  // ✅ Clear intermission ref
  if (intermissionAudioRef.current) {
    try {
      const audio = intermissionAudioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
      intermissionAudioRef.current = null;
    } catch (e) {
      intermissionAudioRef.current = null;
    }
  }
  
  // ✅ Clear audio queue and revoke URLs (if exists)
  if (audioQueueRef.current) {
    audioQueueRef.current.forEach(({ url }) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    });
    audioQueueRef.current.clear();
  }
  
  // ✅ Wait for complete cleanup
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  log(`✅ Nuclear cleanup complete - ${activeAudioElements.current.size} audio elements remaining`);
};
// ============================================
// SKIP TO NEXT CARD - FULLY UPDATED
// ============================================
const skipToNextCard = async () => {
  if (isNavigating) {
    log('⚠️ Navigation already in progress');
    return;
  }

  const nextIndex = currentCardIndex + 1;
  if (nextIndex >= newsCards.length || !isPlaying) return;

  const fromCard = newsCards[currentCardIndex];
  const toCard = newsCards[nextIndex];

  log(`⏭️ Skipping to next card ${currentCardIndex} → ${nextIndex}`);

  // ✅ Mark that user has manually navigated to this card
  hasTransitionedToCard.current.add(nextIndex);

  setIsNavigating(true);
  shouldStopRef.current = true;
  isPausedRef.current = true;

  // ✅ SHOW SHIMMER IMMEDIATELY
  setTranscript([{ voice: 'system', text: 'shimmer', timestamp: Date.now() }]);

  await nuclearCleanupAudio();
  await new Promise(resolve => setTimeout(resolve, 300));

  setCurrentCardIndex(nextIndex);
  setCurrentNewsIndex(nextIndex);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === nextIndex,
    isReady: idx <= nextIndex
  })));

  if (newsCardsRef.current?.children[nextIndex]) {
    newsCardsRef.current.children[nextIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  // ✅ Calculate start line for the next card
  const linesPerNews = Math.floor(scriptLinesRef.current.length / newsCards.length);
  const startLine = nextIndex * linesPerNews;
  const lines = scriptLinesRef.current;

  log('🚀 TRIPLE PARALLEL: Transition + Instant Preload + Warmup...');

  // ✅ FIRE PRELOADS IMMEDIATELY — before awaiting anything
  const preloadPromises = [];
  let preloadCount = 0;

  for (let i = startLine; i < Math.min(startLine + 30, lines.length) && preloadCount < 5; i++) {
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch) {
      const text = guideMatch[1].trim().replace(/\[STORY-\d+\]\s*/g, '');
      log(`📥 INSTANT preload [${preloadCount}]: Guide — "${text.substring(0, 30)}..."`);
      preloadPromises.push(preloadAudio(text, 'guide'));
      preloadCount++;
    } else if (companionMatch) {
      const text = companionMatch[1].trim().replace(/\[STORY-\d+\]\s*/g, '');
      log(`📥 INSTANT preload [${preloadCount}]: Therapist — "${text.substring(0, 30)}..."`);
      preloadPromises.push(preloadAudio(text, 'therapist'));
      preloadCount++;
    } else if (guestMatch) {
      const text = guestMatch[1].trim().replace(/\[STORY-\d+\]\s*/g, '');
      const guestVoice = guestName === 'Kenny' ? 'guest1' : 'guest2';
      log(`📥 INSTANT preload [${preloadCount}]: ${guestName} — "${text.substring(0, 30)}..."`);
      preloadPromises.push(preloadAudio(text, guestVoice));
      preloadCount++;
    }
  }

  // ✅ Generate transition IN PARALLEL with preloads (both fire at same time)
  const transitionPromise = generateCardTransition(fromCard, toCard, true);

  // ✅ Wait for transition text (preloads continue in background)
  const transitionText = await transitionPromise;
  log(`✅ Transition generated (${transitionText.split('\n').length} exchanges)`);

  shouldStopRef.current = false;
  isPausedRef.current = false;
  setIsPaused(false);
  setIsPlaying(true);

  // ✅ Play transition — preloads are warming in background the whole time
  await playTransitionDialogue(transitionText);

  if (shouldStopRef.current) {
    setIsNavigating(false);
    return;
  }

  // ✅ Wait for preloads with a max cap — don't block forever
  await Promise.race([
    Promise.all(preloadPromises),
    new Promise(resolve => setTimeout(resolve, 1500))
  ]);
  await new Promise(resolve => setTimeout(resolve, 50));

  if (activeAudioElements.current.size <= 1 && !shouldStopRef.current) {
    log(`✅ Safe to resume at line ${startLine} (card ${nextIndex})`);
    setIsNavigating(false);
    await resumeFromLine(startLine);
  } else {
    log(`⚠️ Not safe to resume — ${activeAudioElements.current.size} audio elements active`);
    setIsNavigating(false);
  }
};


// ============================================
// SKIP TO PREVIOUS CARD - FULLY UPDATED
// ============================================
const skipToPreviousCard = async () => {
  if (isNavigating) {
    log('⚠️ Navigation already in progress');
    return;
  }

  const prevIndex = currentCardIndex - 1;
  if (prevIndex < 0 || !isPlaying) return;

  const fromCard = newsCards[currentCardIndex];
  const toCard = newsCards[prevIndex];

  log(`⏮️ Going back to card ${currentCardIndex} → ${prevIndex}`);

  // ✅ Mark that user has manually navigated to this card
  hasTransitionedToCard.current.add(prevIndex);

  setIsNavigating(true);
  shouldStopRef.current = true;
  isPausedRef.current = true;

  // ✅ SHOW SHIMMER IMMEDIATELY
  setTranscript([{ voice: 'system', text: 'shimmer', timestamp: Date.now() }]);

  await nuclearCleanupAudio();
  await new Promise(resolve => setTimeout(resolve, 300));

  setCurrentCardIndex(prevIndex);
  setCurrentNewsIndex(prevIndex);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === prevIndex,
    isReady: idx <= prevIndex
  })));

  if (newsCardsRef.current?.children[prevIndex]) {
    newsCardsRef.current.children[prevIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  // ✅ Calculate start line for the previous card
  const linesPerNews = Math.floor(scriptLinesRef.current.length / newsCards.length);
  const startLine = prevIndex * linesPerNews;
  const lines = scriptLinesRef.current;

  log('🚀 TRIPLE PARALLEL: Transition + Instant Preload + Warmup...');

  // ✅ FIRE PRELOADS IMMEDIATELY — before awaiting anything
  const preloadPromises = [];
  let preloadCount = 0;

  for (let i = startLine; i < Math.min(startLine + 30, lines.length) && preloadCount < 5; i++) {
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch) {
      const text = guideMatch[1].trim().replace(/\[STORY-\d+\]\s*/g, '');
      log(`📥 INSTANT preload [${preloadCount}]: Guide — "${text.substring(0, 30)}..."`);
      preloadPromises.push(preloadAudio(text, 'guide'));
      preloadCount++;
    } else if (companionMatch) {
      const text = companionMatch[1].trim().replace(/\[STORY-\d+\]\s*/g, '');
      log(`📥 INSTANT preload [${preloadCount}]: Therapist — "${text.substring(0, 30)}..."`);
      preloadPromises.push(preloadAudio(text, 'therapist'));
      preloadCount++;
    } else if (guestMatch) {
      const text = guestMatch[1].trim().replace(/\[STORY-\d+\]\s*/g, '');
      const guestVoice = guestName === 'Kenny' ? 'guest1' : 'guest2';
      log(`📥 INSTANT preload [${preloadCount}]: ${guestName} — "${text.substring(0, 30)}..."`);
      preloadPromises.push(preloadAudio(text, guestVoice));
      preloadCount++;
    }
  }

  // ✅ Generate transition IN PARALLEL with preloads (both fire at same time)
  const transitionPromise = generateCardTransition(fromCard, toCard, false);

  // ✅ Wait for transition text (preloads continue in background)
  const transitionText = await transitionPromise;
  log(`✅ Transition generated (${transitionText.split('\n').length} exchanges)`);

  shouldStopRef.current = false;
  isPausedRef.current = false;
  setIsPaused(false);
  setIsPlaying(true);

  // ✅ Play transition — preloads are warming in background the whole time
  await playTransitionDialogue(transitionText);

  if (shouldStopRef.current) {
    setIsNavigating(false);
    return;
  }

  // ✅ Wait for preloads with a max cap — don't block forever
  await Promise.race([
    Promise.all(preloadPromises),
    new Promise(resolve => setTimeout(resolve, 1500))
  ]);
  await new Promise(resolve => setTimeout(resolve, 50));

  if (activeAudioElements.current.size <= 1 && !shouldStopRef.current) {
    log(`✅ Safe to resume at line ${startLine} (card ${prevIndex})`);
    setIsNavigating(false);
    await resumeFromLine(startLine);
  } else {
    log(`⚠️ Not safe to resume — ${activeAudioElements.current.size} audio elements active`);
    setIsNavigating(false);
  }
};
// ============================================
// ANALYZE STORY CONTENT - Extract keywords for matching
// ============================================
const analyzeStoryContent = () => {
  const storyKeywords = newsCards.map((card, idx) => ({
    index: idx,
    title: card.fullTitle.toLowerCase(),
    snippet: card.snippet.toLowerCase(),
    source: card.source.toLowerCase(),
    keywords: [
      ...card.fullTitle.toLowerCase().split(/\s+/).slice(0, 5),
      ...card.source.toLowerCase().split(/\s+/)
    ]
  }));

  return storyKeywords;
};
// ============================================
// CARD DETECTION HELPERS
// ============================================

const findMatchingCard = (recentTranscript) => {
  const transcriptLower = recentTranscript.toLowerCase();
  
  for (let i = 0; i < newsCards.length; i++) {
    const card = newsCards[i];
    
    // Strategy 1: Exact title match
    if (transcriptLower.includes(card.fullTitle.toLowerCase())) {
      log(`  ✅ EXACT TITLE: Card ${i}`);
      return i;
    }
    
    // Strategy 2: Title words match (25%+ overlap) ✅ LOWERED FROM 40%
    const titleWords = card.fullTitle
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(w => w.length > 3);
    
    const matchedWords = titleWords.filter(w => transcriptLower.includes(w));
    const matchRatio = matchedWords.length / titleWords.length;
    
    if (matchRatio >= 0.25 && matchedWords.length >= 1) { // ✅ LOWERED threshold + min 1 word
      log(`  ✅ TITLE WORDS (${Math.round(matchRatio * 100)}%): Card ${i}`);
      return i;
    }
    
    // Strategy 3: Source match
    const sourceWords = card.source.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    if (sourceWords.some(w => transcriptLower.includes(w))) {
      log(`  ✅ SOURCE: Card ${i}`);
      return i;
    }
  }
  
  return null;
};

const detectTransition = (recentTranscript) => {
  const transitions = [
    'next up', 'moving on', 'next story', 'another one',
    'alright so', 'next we have', 'shifting to', 'now let\'s'
  ];
  
  return transitions.some(t => recentTranscript.toLowerCase().includes(t));
};

const detectCurrentCard = (recentTranscript, lastCard) => {
  // Try to find matching card
  const match = findMatchingCard(recentTranscript);
  
  if (match !== null) {
    return match;
  }
  
  // Check for transition
  if (detectTransition(recentTranscript) && lastCard < newsCards.length - 1) {
    log(`  🔄 TRANSITION → Card ${lastCard + 1}`);
    return lastCard + 1;
  }
  
  // No change
  return lastCard;
};
// ============================================
// MARKER-BASED CARD DETECTION
// ============================================

const detectStoryMarker = (text) => {
  // Detect [STORY-X] pattern
  const markerMatch = text.match(/\[STORY-(\d+)\]/);
  
  if (markerMatch) {
    const storyNumber = parseInt(markerMatch[1], 10);
    return storyNumber - 1; // Convert to 0-based index
  }
  
  return null;
};
// ============================================
// 4️⃣ SMART CARD SYNC - ANALYZES CONTENT
// ============================================
// ============================================

const playScript = async (script) => {
  const lines = script.split('\n').filter(line => line.trim());
  scriptLinesRef.current = lines;
  
  log(`📝 Script has ${lines.length} lines, ${newsCards.length} news items`);
  
  const cardMap = {};
  const linesPerCard = Math.floor(lines.length / newsCards.length);
  
  for (let i = 0; i < newsCards.length; i++) {
    const startLine = i * linesPerCard;
    const endLine = i === newsCards.length - 1 ? lines.length : (i + 1) * linesPerCard;
    
    cardMap[i] = { start: startLine, end: endLine };
  }
  
  setScriptToCardMap(cardMap);
  
  let currentCard = 0;
  
  for (let i = 0; i < lines.length; i++) {
    currentLineIndexRef.current = i;
    
    if (shouldStopRef.current) break;
    
    while (isPausedRef.current && !shouldStopRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    if (shouldStopRef.current) break;
    
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch || companionMatch || guestMatch) {
      let voice, text;
      
      if (guideMatch) {
        voice = 'guide';
        text = guideMatch[1].trim();
      } else if (companionMatch) {
        voice = 'therapist';
        text = companionMatch[1].trim();
      } else if (guestMatch) {
        voice = guestName === 'Kenny' ? 'guest1' : 'guest2';
        text = guestMatch[1].trim();
      }

      // ✅ DETECT STORY MARKER
      const detectedStory = detectStoryMarker(text);
      
if (detectedStory !== null && detectedStory !== currentCard) {
  log(`🎯 MARKER DETECTED: [STORY-${detectedStory + 1}] → Card ${detectedStory}`);

  // ✅ BLOCK: If user manually jumped AHEAD of this marker, never go back
  const userIsAheadOfMarker = hasTransitionedToCard.current.size > 0 && 
    currentCardIndex > detectedStory;

  if (userIsAheadOfMarker) {
    log(`⛔ BLOCKED marker rollback: user is at card ${currentCardIndex}, marker wants card ${detectedStory} — skipping`);
    const cleanText = text.replace(/\[STORY-\d+\]\s*/g, '');
    await playAudio(cleanText, voice);
    continue;
  }

  if (hasTransitionedToCard.current.has(detectedStory)) {
    log(`⏭️ SKIPPING scripted transition - user already navigated here manually`);
    currentCard = detectedStory;
    setCurrentCardIndex(detectedStory);
    setCurrentNewsIndex(detectedStory);
    setNewsCards(prev => prev.map((card, idx) => ({
      ...card,
      isActive: idx === detectedStory,
      isReady: idx <= detectedStory
    })));
    if (newsCardsRef.current?.children[detectedStory]) {
      newsCardsRef.current.children[detectedStory].scrollIntoView({
        behavior: 'smooth', block: 'nearest', inline: 'center'
      });
    }
    const cleanText = text.replace(/\[STORY-\d+\]\s*/g, '');
    const isScriptedTransition = /moving on|next up|alright so|next story|next we have|shifting to|now let's/i.test(cleanText.toLowerCase());
    if (isScriptedTransition) {
      log(`⏭️ SKIPPING scripted transition line: "${cleanText.substring(0, 50)}..."`);
      continue;
    }
    await playAudio(cleanText, voice);
  } else {
    log(`✅ Playing scripted transition for Card ${detectedStory}`);
    currentCard = detectedStory;
    setCurrentCardIndex(detectedStory);
    setCurrentNewsIndex(detectedStory);
    setNewsCards(prev => prev.map((card, idx) => ({
      ...card,
      isActive: idx === detectedStory,
      isReady: idx <= detectedStory
    })));
    if (newsCardsRef.current?.children[detectedStory]) {
      newsCardsRef.current.children[detectedStory].scrollIntoView({
        behavior: 'smooth', block: 'nearest', inline: 'center'
      });
    }
    const cleanText = text.replace(/\[STORY-\d+\]\s*/g, '');
    await playAudio(cleanText, voice);
  }
      } else {
        // ✅ REMOVE MARKER FROM TEXT BEFORE SPEAKING
        const cleanText = text.replace(/\[STORY-\d+\]\s*/g, '');
        await playAudio(cleanText, voice);
      }
      
      if (shouldStopRef.current) break;
    }
  }
  
log('✅ Script playback complete');

// ✅ NATURAL WRAP-UP after last card finishes
if (!shouldStopRef.current) {
  log('🎙️ Generating natural wrap-up summary...');
  
  const lastCard = newsCards[newsCards.length - 1];
  const wrapUpPrompt = `You are wrapping up a live news station. The last story discussed was "${lastCard?.fullTitle || 'the final story'}".

Create a BRIEF natural wrap-up (2-3 sentences) that:
- Briefly recaps the key takeaway from today's coverage in ONE sentence
- Thanks the listener for tuning in (say "you", not their name)
- Invites them back tomorrow

Output ONLY the wrap-up, nothing else.`;

  try {
    const wrapData = await SecureAPI.callAzureOpenAI(
      [
        { role: 'system', content: 'You are a warm broadcast host wrapping up a show naturally.' },
        { role: 'user', content: wrapUpPrompt }
      ],
      { temperature: 0.88, max_tokens: 100 }
    );
    const wrapText = wrapData.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
    const wrapVoice = Math.random() < 0.5 ? 'guide' : 'therapist';
    await playAudio(wrapText, wrapVoice);
  } catch (err) {
    log(`⚠️ Wrap-up generation failed: ${err.message}`);
    await playAudio(`That covers everything for today. Thanks for tuning in, and we will see you tomorrow.`, 'guide');
  }
}};



// ============================================
// GENERATE NATURAL MULTI-HOST CARD TRANSITION
// ============================================

const generateCardTransition = async (fromCard, toCard, isNext = true) => {
  const direction = isNext ? 'next' : 'previous';
  
  const transitionPrompt = `You are creating a natural multi-host conversation transitioning between news stories.

**FROM:** ${fromCard.fullTitle}
**TO:** ${toCard.fullTitle}
**TO SNIPPET:** ${toCard.snippet}
**TO SOURCE:** ${toCard.source}
**DIRECTION:** ${direction === 'next' ? 'forward to next story' : 'back to previous story'}

Create a CONVERSATIONAL HANDOFF between Guide and Companion (2-3 exchanges, total 60-80 words):

**STRUCTURE:**
Guide: [Brief transition hook - 15-20 words]
Companion: [Quick reaction/question - 8-12 words]
Guide: [Context about the story - 20-30 words]

**RULES:**
- Guide starts the transition naturally
- Companion reacts or asks a follow-up
- Guide provides more context about ${toCard.source}'s story
- Reference the snippet content WITHOUT spoiling details
- Make it feel like real-time discussion
- NO "next up" phrases - be conversational
- Mention ${toCard.source} naturally
- Each line should be BRIEF (8-20 words max)

**GOOD EXAMPLES:**

Guide: "So moving forward, ${toCard.source} just broke something pretty significant here"
Companion: "Oh yeah, what are they reporting"
Guide: "They're looking at how this new strategy could reshape the whole sector. The timing is really interesting because it touches on long-term positioning"

Guide: "Alright, ${toCard.source} has this development that's catching attention"
Companion: "What makes it compelling"
Guide: "It's about innovation in a space that's been pretty stagnant. The implications here are actually pretty big for how things move forward"

Guide: "Going back to this one from ${toCard.source}"
Companion: "Yeah, what's the story"
Guide: "They're focusing on a major pivot in their approach. What's interesting is the timing—it's happening right as the market's shifting"

**OUTPUT FORMAT:**
Guide: [text]
Companion: [text]
Guide: [text]

Output ONLY the dialogue, nothing else.`;

  try {
    const response = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: 'You create natural, conversational multi-host transitions. Make it feel like real-time discussion between two engaged hosts, not scripted transitions. Keep each line brief (8-20 words).'
        },
        { role: 'user', content: transitionPrompt }
      ],
      {
        temperature: 0.88,
        max_tokens: 200
      }
    );

    return response.choices[0].message.content.trim();
  } catch (error) {
    log(`⚠️ Transition generation failed: ${error.message}`);
    // Fallback multi-host transition
    return `Guide: So ${toCard.source} has this development that's worth looking at\nCompanion: What's the story\nGuide: They're reporting on ${toCard.fullTitle.split(' ').slice(0, 6).join(' ')}. This could have some interesting implications`;
  }
};

// ============================================
// PLAY MULTI-HOST TRANSITION (ONE LINE AT A TIME)
// ============================================

const playTransitionDialogue = async (transitionText) => {
  if (!transitionText) return;
  
  isPlayingTransition.current = true;
  
  // Parse the multi-line transition
  const lines = transitionText.split('\n').filter(line => line.trim());
  
  log(`🎭 Playing ${lines.length}-part transition...`);
  
  for (let i = 0; i < lines.length; i++) {
    if (shouldStopRef.current) {
      isPlayingTransition.current = false;
      return;
    }
    
    // Check for pause
    while (isPausedRef.current && !shouldStopRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    if (shouldStopRef.current) {
      isPlayingTransition.current = false;
      return;
    }
    
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    
    if (guideMatch) {
      const text = guideMatch[1].trim();
      log(`  🎙️ Guide: "${text}"`);
      await playAudio(text, 'guide');
    } else if (companionMatch) {
      const text = companionMatch[1].trim();
      log(`  🎙️ Companion: "${text}"`);
      await playAudio(text, 'therapist');
    }
    
    if (shouldStopRef.current) {
      isPlayingTransition.current = false;
      return;
    }
  }
  
  isPlayingTransition.current = false;
  log(`✅ Transition complete`);
};

// ============================================
// 5️⃣ CLEANUP ON UNMOUNT
// ============================================
useEffect(() => {
  return () => {
    shouldStopRef.current = true;
    isPausedRef.current = true;
    
    try {
      abortControllerRef.current.abort();
    } catch (e) {}
    
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    if (intermissionAudioRef.current) {
      intermissionAudioRef.current.pause();
      intermissionAudioRef.current = null;
    }
    
    audioQueueRef.current.forEach(({ url }) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    });
    audioQueueRef.current.clear();
    isPreloadingRef.current.clear();
  };
}, []);
const selectRandomGuest = () => {
  const guests = [
    { name: 'Kenny', voice: 'guest1' },
    { name: 'Jackie', voice: 'guest2' }
  ];
  return guests[Math.floor(Math.random() * guests.length)];
};

const shouldIncludeGuest = () => {
  // 60% chance to include guest (works for first cycle and all cycles)
  return Math.random() < 0.6;
};


const [sessionData, setSessionData] = useState({
  startedAt: null,
  lastPausedAt: null,
  lastLineIndex: 0,
  currentNewsIndex: 0,
  company: null,
  date: null
});
useEffect(() => {
  if (transcriptEndRef.current) {
    // ✅ Scroll to top (where latest transcript is)
    transcriptEndRef.current.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }
}, [transcript]);
const skipToNextNews = async () => {
  const nextIndex = currentNewsIndex + 1;
  
  if (nextIndex >= totalNewsCount || !preGeneratedRef.current.has(nextIndex)) {
    log(`⏳ Next segment not ready yet`);
    return;
  }
  
  log(`⏭️ Skipping to next news (${currentNewsIndex} → ${nextIndex})`);
  
  shouldStopRef.current = true;
  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current = null;
  }
  
  await new Promise(resolve => setTimeout(resolve, 100));
  
  setCurrentNewsIndex(nextIndex);
  
  const nextSegment = newsSegmentsRef.current[nextIndex];
  if (nextSegment) {
    shouldStopRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    resumeFromLine(nextSegment.start);
  }
};

const skipToPreviousNews = async () => {
  const prevIndex = currentNewsIndex - 1;
  
  if (prevIndex < 0) {
    log(`⏮️ Already at first segment`);
    return;
  }
  
  log(`⏮️ Skipping to previous news (${currentNewsIndex} → ${prevIndex})`);
  
  shouldStopRef.current = true;
  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current = null;
  }
  
  await new Promise(resolve => setTimeout(resolve, 100));
  
  setCurrentNewsIndex(prevIndex);
  
  const prevSegment = newsSegmentsRef.current[prevIndex];
  if (prevSegment) {
    shouldStopRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    resumeFromLine(prevSegment.start);
  }
};
const handleArticleClick = (card) => {
  setSelectedArticle(card);
  setShowArticleConfirm(true);
};

const cancelArticle = () => {
  setShowArticleConfirm(false);
  setSelectedArticle(null);
};

const confirmArticle = () => {
  if (selectedArticle?.url) {
    window.open(selectedArticle.url, '_blank');
  }
  setShowArticleConfirm(false);
  setSelectedArticle(null);
};
const convertNewsToCards = (newsData) => {
  return newsData.map((article, idx) => ({
    id: idx,
    title: article.title.split(' ').slice(0, 4).join(' '),
    fullTitle: article.title,
    image: article.imageUrl || '/image/studycard10.png',
    source: article.publisher,
    url: article.url,
    time: article.publishedAt,
    isReady: false,
    isActive: false,
    snippet: article.summary
  }));
};
const generateShortTitle = (fullTitle) => {
  // Remove common prefixes
  let cleaned = fullTitle
    .replace(/^(Breaking:|LIVE:|Update:|News:)/i, '')
    .trim();
  
  // Split into words
  const words = cleaned.split(/\s+/);
  
  // Take first 2-3 meaningful words
  const shortWords = words.slice(0, 3).join(' ');
  
  // If too long, truncate
  if (shortWords.length > 25) {
    return shortWords.substring(0, 22) + '...';
  }
  
  return shortWords;
};
// ============================================
// SAVE SESSION DATA TO STORAGE
// ============================================
const saveSessionData = (lineIndex, newsIndex, scriptLinesRef, totalNewsCount) => {
  try {
    const data = {
      startedAt: new Date().toISOString(),
      lastPausedAt: new Date().toISOString(),
      lastLineIndex: lineIndex,
      currentNewsIndex: newsIndex,
      currentCardIndex: currentCardIndex, // ✅ SAVE CARD POSITION
      currentNewsTitle: newsCards[newsIndex]?.fullTitle || '',
      company: company.ticker,
      date: new Date().toDateString(),
      totalLines: scriptLinesRef.current?.length || 0,
      totalNewsCount: totalNewsCount,
      isComplete: false,
      newsCards: newsCards, // ✅ SAVE ALL CARDS STATE
      hasGuest: hasGuest, // ✅ SAVE GUEST STATE
      guestName: guestName // ✅ SAVE GUEST NAME
    };
    
    // ✅ ALSO SAVE TO FIREBASE
    storageService.saveStationSession(company.ticker, data);
    
    localStorage.setItem(`station_session_${company.ticker}`, JSON.stringify(data));
    log(`✅ Session saved: line ${lineIndex}/${data.totalLines}, news ${newsIndex}/${totalNewsCount}, card ${currentCardIndex}${hasGuest ? `, guest: ${guestName}` : ''}`);
  } catch (error) {
    log(`⚠️ Could not save session: ${error.message}`);
  }
};


const getSessionData = () => {
  try {
    const stored = localStorage.getItem(`station_session_${company.ticker}`);
    if (stored) {
      const data = JSON.parse(stored);
      log(`📍 Found session: line ${data.lastLineIndex}/${data.totalLines}, news ${data.currentNewsIndex}/${data.totalNewsCount}`);
      return data;
    }
  } catch (error) {
    log(`⚠️ Could not load session: ${error.message}`);
  }
  return null;
};

const clearSessionData = () => {
  try {
    localStorage.removeItem(`station_session_${company.ticker}`);
    log('🗑️ Session cleared');
  } catch (error) {
    log(`⚠️ Could not clear session: ${error.message}`);
  }
};


const skipToNewsIndex = async (index) => {
  if (index === currentNewsIndex || !preGeneratedRef.current.has(index)) return;
  
  log(`⏭️ Skipping to news ${index}`);
  
  shouldStopRef.current = true;
  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current = null;
  }
  
  await new Promise(resolve => setTimeout(resolve, 100));
  
  setCurrentNewsIndex(index);
  
  const targetSegment = newsSegmentsRef.current[index];
  if (targetSegment) {
    shouldStopRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    resumeFromLine(targetSegment.start);
  }
};
useEffect(() => {
  if (!isPlaying || transcript.length > 0) return;
  
  const messages = [
    `Searching recent ${cleanCompanyName(company.name)} updates...`,
    `Building your ${cleanCompanyName(company.name)} briefing...`
  ];
  
  let index = 0;
  const interval = setInterval(() => {
    index = (index + 1) % messages.length;
    setLoadingMessage(messages[index]);
  }, 2000); // Change every 2 seconds
  
  return () => clearInterval(interval);
}, [isPlaying, transcript.length, company.name]);
// Update as segments become ready:
useEffect(() => {
  const updated = newsSources.map(source => ({
    ...source,
    isReady: preGeneratedRef.current.has(source.id)
  }));
  setNewsSources(updated);
}, [preGeneratedRef.current]);


useEffect(() => {
  if (autoScrollTranscript && transcriptEndRef.current) {
    transcriptEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}, [transcript, autoScrollTranscript]);
  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
    if (currentAudioRef.current) {
      currentAudioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Check if user has listened today
  useEffect(() => {
    const lastListenDate = localStorage.getItem(`station_${company.ticker}_lastListen`);
    const today = new Date().toDateString();
    
    if (lastListenDate === today) {
      setHasListenedToday(true);
    }
  }, [company.ticker]);

  const log = useCallback((message) => {
    console.log(`[Station] ${message}`);
  }, []);
  
// Store in localStorage so it persists across sessions
const getLastUsedMusic = (type) => {
  try {
    const stored = localStorage.getItem(`lastUsed${type}Music`);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const setLastUsedMusic = (type, url) => {
  try {
    localStorage.setItem(`lastUsed${type}Music`, JSON.stringify(url));
  } catch {
    log('⚠️ Could not save music preference');
  }
};

const selectRandomMusicNeverRepeat = (musicOptions, type) => {
  const lastUsed = getLastUsedMusic(type);
  
  // Filter out the last used one
  const available = musicOptions.filter(url => url !== lastUsed);
  
  // If somehow we have no options, use all (shouldn't happen)
  const choices = available.length > 0 ? available : musicOptions;
  
  // Pick random from available
  const selected = choices[Math.floor(Math.random() * choices.length)];
  
  // Save for next time
  setLastUsedMusic(type, selected);
  
  log(`🎵 Selected ${type} music (avoiding repeat of ${lastUsed?.split('/').pop() || 'none'})`);
  
  return selected;
};
const trimSilenceAggressive = async (blob) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // ✅ AGGRESSIVE: Very low threshold to catch any silence
    const threshold = 0.005; // 0.5% volume (very sensitive)
    const sampleRate = audioBuffer.sampleRate;
    const channels = audioBuffer.numberOfChannels;
    
    // Find first non-silent sample
    let start = 0;
    const channel0 = audioBuffer.getChannelData(0);
    
    // ✅ Scan for first sound (no padding on start)
    for (let i = 0; i < channel0.length; i++) {
      if (Math.abs(channel0[i]) > threshold) {
        start = i; // ✅ No padding - cut right at sound start
        break;
      }
    }
    
    // Find last non-silent sample
    let end = channel0.length;
    
    // ✅ Scan backwards for last sound (no padding on end)
    for (let i = channel0.length - 1; i >= start; i--) {
      if (Math.abs(channel0[i]) > threshold) {
        end = i + 1; // ✅ No padding - cut right after sound ends
        break;
      }
    }
    
    // Create trimmed buffer
    const trimmedLength = end - start;
    if (trimmedLength <= 0) return blob;
    
    const trimmedBuffer = audioContext.createBuffer(channels, trimmedLength, sampleRate);
    
    for (let ch = 0; ch < channels; ch++) {
      const sourceData = audioBuffer.getChannelData(ch);
      const targetData = trimmedBuffer.getChannelData(ch);
      targetData.set(sourceData.slice(start, end));
    }
    
    const originalDuration = (audioBuffer.length / sampleRate).toFixed(3);
    const trimmedDuration = (trimmedLength / sampleRate).toFixed(3);
    const saved = (originalDuration - trimmedDuration).toFixed(3);
    
    if (saved > 0.05) {
      log(`✂️ Aggressive trim: ${saved}s removed (${originalDuration}s → ${trimmedDuration}s)`);
    }
    
    const trimmedBlob = audioBufferToWavBlob(trimmedBuffer);
    await audioContext.close();
    
    return trimmedBlob;
  } catch (error) {
    log(`⚠️ Aggressive trim failed: ${error.message}`);
    return blob;
  }
};

const selectRandomMusic = (musicOptions, lastUsed) => {
  let selected;
  do {
    selected = musicOptions[Math.floor(Math.random() * musicOptions.length)];
  } while (selected === lastUsed && musicOptions.length > 1);
  
  return selected;
};

const audioBufferToWavBlob = (audioBuffer) => {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const length = audioBuffer.length;
  
  // Interleave channels
  const interleaved = new Float32Array(length * numberOfChannels);
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      interleaved[i * numberOfChannels + channel] = audioBuffer.getChannelData(channel)[i];
    }
  }
  
  // Convert to 16-bit PCM
  const pcmData = new Int16Array(interleaved.length);
  for (let i = 0; i < interleaved.length; i++) {
    const sample = Math.max(-1, Math.min(1, interleaved[i]));
    pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
  }
  
  // Create WAV file
  const wavBuffer = new ArrayBuffer(44 + pcmData.length * 2);
  const view = new DataView(wavBuffer);
  
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + pcmData.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numberOfChannels * 2, true);
  view.setUint16(32, numberOfChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, pcmData.length * 2, true);
  
  const dataView = new Int16Array(wavBuffer, 44);
  dataView.set(pcmData);
  
  return new Blob([wavBuffer], { type: 'audio/wav' });
};

// ============================================
// PLAY AUDIO WITH ULTRA-AGGRESSIVE TRIMMING
// ============================================

const playAudio = async (text, voice) => {
  if (shouldStopRef.current) return;

  // ✅ Replace shimmer with real transcript when audio starts
  setTranscript(prev => {
    const withoutShimmer = prev.filter(item => item.text !== 'shimmer');
    return [...withoutShimmer, { voice, text, timestamp: Date.now() }];
  });

  const cacheKey = `${voice}-${text}`;
  const cached = audioQueueRef.current.get(cacheKey);

  if (cached && !cached.used) {
    cached.used = true;

    return new Promise((resolve) => {
      if (shouldStopRef.current) {
        resolve();
        return;
      }
      
      const audio = new Audio(cached.url);
      
      // ✅ Stop previous audio SAFELY
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          activeAudioElements.current.delete(currentAudioRef.current);
        } catch (e) {}
      }
      
      // ✅ REGISTER IN GUEST LIST
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(cached.url);
        audioQueueRef.current.delete(cacheKey);
        resolve();
      };

      audio.onerror = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        resolve();
      };

      // ✅ SAFE PLAY - Handle autoplay policy
      const playPromise = audio.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(error => {
          // ✅ IGNORE "interrupted by pause" errors
          if (error.name === 'NotAllowedError' || error.message.includes('interrupted')) {
            log(`⚠️ Play interrupted (likely by pause) - this is OK`);
            activeAudioElements.current.delete(audio);
            currentAudioRef.current = null;
            resolve();
          } else {
            log(`❌ Play error: ${error.message}`);
            activeAudioElements.current.delete(audio);
            currentAudioRef.current = null;
            resolve();
          }
        });
      }
    });
  }

  // Generate live if not cached
  try {
    if (shouldStopRef.current) return;
    
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      playbackSpeedRef.current
    );

    const trimmedBlob = await trimSilenceAggressive(audioBlob);
    const audioUrl = URL.createObjectURL(trimmedBlob);

    return new Promise((resolve) => {
      if (shouldStopRef.current) {
        URL.revokeObjectURL(audioUrl);
        resolve();
        return;
      }
      
      const audio = new Audio(audioUrl);
      
      // ✅ Stop previous audio SAFELY
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          activeAudioElements.current.delete(currentAudioRef.current);
        } catch (e) {}
      }
      
      // ✅ REGISTER IN GUEST LIST
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        resolve();
      };

      audio.onerror = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      
      // ✅ SAFE PLAY - Handle autoplay policy
      const playPromise = audio.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(error => {
          // ✅ IGNORE "interrupted by pause" errors
          if (error.name === 'NotAllowedError' || error.message.includes('interrupted')) {
            log(`⚠️ Play interrupted (likely by pause) - this is OK`);
            activeAudioElements.current.delete(audio);
            currentAudioRef.current = null;
            resolve();
          } else {
            log(`❌ Play error: ${error.message}`);
            activeAudioElements.current.delete(audio);
            currentAudioRef.current = null;
            resolve();
          }
        });
      }
    });
  } catch (error) {
    log(`Audio error: ${error.message}`);
  }
};



// ============================================
// PLAY INTRO MUSIC - NO REPEAT
// ============================================

const playIntroMusicWithFade = async (duration = 10) => {
  try {
    // ✅ Never play same music twice
    const musicUrl = selectRandomMusicNeverRepeat(INTRO_MUSIC_OPTIONS, 'Intro');
    
    log(`🎵 Playing intro music: ${musicUrl.split('/').pop()}`);
    
    const audio = new Audio(musicUrl);
    audio.volume = 0.4;
    audio.crossOrigin = 'anonymous';
    currentAudioRef.current = audio;
    
    return new Promise((resolve) => {
      audio.oncanplaythrough = () => {
        audio.play().catch(e => log(`⚠️ Auto-play blocked: ${e.message}`));
        
        // After duration, fade out
        setTimeout(async () => {
          if (!shouldStopRef.current) {
            await fadeOutMusicIntoHosts(audio, 800);
          }
          resolve();
        }, duration * 1000);
      };
      
      audio.onerror = () => {
        log(`⚠️ Intro music failed to load`);
        resolve();
      };
      
      audio.load();
    });
  } catch (error) {
    log(`❌ Intro music error: ${error.message}`);
  }
};

// ============================================
// PLAY INTERMISSION MUSIC - NO REPEAT
// ============================================

const playIntermissionMusicWithFade = async () => {
  try {
    // ✅ CHECK if paused/stopped BEFORE starting
    if (shouldStopRef.current || isPausedRef.current) {
      log('⏸️ Skipping intermission music - paused/stopped');
      return;
    }
    
    const musicUrl = selectRandomMusicNeverRepeat(INTERMISSION_MUSIC_OPTIONS, 'Intermission');
    const audio = new Audio(musicUrl);
    audio.volume = 0.3;
    audio.loop = false; // ✅ DON'T loop
    audio.crossOrigin = 'anonymous';
    
    // ✅ REGISTER IN GUEST LIST
    activeAudioElements.current.add(audio);
    currentAudioRef.current = audio;
    intermissionAudioRef.current = audio;
    
    return new Promise((resolve) => {
      audio.oncanplaythrough = () => {
        // ✅ CHECK AGAIN before playing
        if (shouldStopRef.current || isPausedRef.current) {
          activeAudioElements.current.delete(audio);
          currentAudioRef.current = null;
          intermissionAudioRef.current = null;
          log('⏸️ Cancelled intermission music - paused');
          resolve();
          return;
        }
        
        log('🎵 Intermission music started, will play for exactly 5 seconds...');
        audio.play().catch(e => log(`⚠️ Auto-play blocked: ${e.message}`));
        
        // ✅ FADE OUT AFTER EXACTLY 5 SECONDS
        const fadeOutStart = 4500; // Start fade at 4.5 seconds (500ms fade)
        
        setTimeout(() => {
          if (!shouldStopRef.current && audio && audio.src) {
            log('🎵 Intermission music fade-out starting...');
            fadeOutMusicIntoHosts(audio, 500).then(() => {
              activeAudioElements.current.delete(audio);
              intermissionAudioRef.current = null;
              resolve();
            });
          }
        }, fadeOutStart);
        
        // ✅ SAFETY: Stop after 6 seconds in case fade doesn't trigger
        setTimeout(() => {
          if (intermissionAudioRef.current && intermissionAudioRef.current === audio) {
            try {
              audio.pause();
              audio.currentTime = 0;
              audio.volume = 0;
              activeAudioElements.current.delete(audio);
              intermissionAudioRef.current = null;
            } catch (e) {}
          }
        }, 6000);
      };
      
      audio.onerror = () => {
        log(`⚠️ Intermission music failed to load`);
        activeAudioElements.current.delete(audio);
        intermissionAudioRef.current = null;
        resolve();
      };
      
      audio.load();
    });
  } catch (error) {
    log(`❌ Intermission music error: ${error.message}`);
    intermissionAudioRef.current = null;
  }
};


// ============================================
// FADE OUT MUSIC - NO GAPS
// ============================================


const fadeOutMusicIntoHosts = async (audioElement, fadeOutDuration = 500) => {
  if (!audioElement) return;
  
  return new Promise((resolve) => {
    const startVolume = audioElement.volume;
    const steps = 50;
    const volumeStep = startVolume / steps;
    const timeStep = fadeOutDuration / steps;
    let currentStep = 0;
    
    log(`🎵 Fading out over ${fadeOutDuration}ms (${steps} steps)...`);
    
    const fadeInterval = setInterval(() => {
      if (currentStep >= steps || shouldStopRef.current) {
        audioElement.pause();
        audioElement.currentTime = 0;
        audioElement.volume = 0;
        audioElement.src = '';
        audioElement.load();
        currentAudioRef.current = null;
        activeAudioElements.current.delete(audioElement);
        log('🎵 Fade-out complete');
        clearInterval(fadeInterval);
        resolve();
        return;
      }
      
      if (audioElement) {
        audioElement.volume = Math.max(0, startVolume - (volumeStep * currentStep));
      }
      currentStep++;
    }, timeStep);
  });
};

const trimSilenceFromBlob = async (blob) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    const threshold = 0.01; // 1% volume threshold
    const sampleRate = audioBuffer.sampleRate;
    const channels = audioBuffer.numberOfChannels;
    
    // Find first non-silent sample
    let start = 0;
    const channel0 = audioBuffer.getChannelData(0);
    for (let i = 0; i < channel0.length; i++) {
      if (Math.abs(channel0[i]) > threshold) {
        start = Math.max(0, i - Math.floor(0.05 * sampleRate)); // Keep 50ms padding
        break;
      }
    }
    
    // Find last non-silent sample
    let end = channel0.length;
    for (let i = channel0.length - 1; i >= start; i--) {
      if (Math.abs(channel0[i]) > threshold) {
        end = Math.min(channel0.length, i + Math.floor(0.05 * sampleRate)); // Keep 50ms padding
        break;
      }
    }
    
    // Create trimmed buffer
    const trimmedLength = end - start;
    if (trimmedLength <= 0) return blob;
    
    const trimmedBuffer = audioContext.createBuffer(channels, trimmedLength, sampleRate);
    
    for (let ch = 0; ch < channels; ch++) {
      const sourceData = audioBuffer.getChannelData(ch);
      const targetData = trimmedBuffer.getChannelData(ch);
      targetData.set(sourceData.slice(start, end));
    }
    
    // Convert back to blob
    const trimmedBlob = audioBufferToWavBlob(trimmedBuffer);
    await audioContext.close();
    
    return trimmedBlob;
  } catch (error) {
    log(`⚠️ Silence trim failed: ${error.message}, using original`);
    return blob;
  }
};

const forceStopIntermissionMusic = async () => {
  if (currentAudioRef.current) {
    try {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current.src = '';
      currentAudioRef.current.load();
      currentAudioRef.current.volume = 0;
      currentAudioRef.current = null;
      log('✅ Intermission music force stopped');
    } catch (e) {
      log(`⚠️ Error stopping music: ${e.message}`);
      currentAudioRef.current = null;
    }
  }
};
  // Fetch latest company news from Yahoo Finance
const generateFallbackNews = () => {
  const timeOfDay = getTimeOfDay();
  
  return [
    {
      title: `${cleanCompanyName(company.name)} continues market momentum in ${timeOfDay} trading`,
      publisher: 'Market Watch',
      summary: `${cleanCompanyName(company.name)} (${company.ticker}) shows steady performance in today's market session with investors closely monitoring key developments.`,
      publishedAt: new Date(),
      imageUrl: '/image/studycard12.png',
      url: '#'
    },
    {
      title: `Analysts discuss ${cleanCompanyName(company.name)}'s strategic positioning`,
      publisher: 'Financial Times',
      summary: `Industry experts weigh in on ${cleanCompanyName(company.name)}'s recent business strategies and their potential impact on the technology sector.`,
      publishedAt: new Date(Date.now() - 3600000),
      imageUrl: '/image/studycard14.png',
      url: '#'
    },
    {
      title: `${cleanCompanyName(company.name)} maintains focus on innovation and growth`,
      publisher: 'Tech News',
      summary: `${cleanCompanyName(company.name)} continues to push forward with its innovation agenda, maintaining its position as a key player in the industry.`,
      publishedAt: new Date(Date.now() - 7200000),
      imageUrl: '/image/studycard13.png',
      url: '#'
    }
  ];
};
// ============================================
// IMPROVED fetchCompanyNews WITH ARTICLE TRACKING
// ============================================
// Tracks which articles have been shown to avoid repetition
// Prioritizes new articles from The Montclarion first
// Uses localStorage to remember shown articles
const fetchCompanyNews = async () => {
  try {
    // ✅ SPECIAL HANDLING FOR MSU
    if (company.ticker === 'MSU' || company.isLocal) {
      log('🎓 Fetching FRESH Montclair State University news...');
      
      const MSU_BACKEND_URL = 'https://us-central1-fir-7c9c9.cloudfunctions.net/getMSUNews';
      
      try {
        // ✅ FORCE FRESH with cache-busting
        const cacheBuster = Date.now();
        const response = await fetch(`${MSU_BACKEND_URL}?forceRefresh=true&t=${cacheBuster}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        log(`MSU API Response status: ${response.status}`);
        
        if (response.ok) {
          const data = await response.json();
          
          const results = data.results || data.data || data.articles || data.news || [];
          const isSuccess = data.success !== false;
          
          log(`Parsed: success=${isSuccess}, results=${results.length} items`);
          
          if (Array.isArray(results) && results.length > 0) {
            log(`✅ Found ${results.length} MSU news items from API`);
            
            const articlesWithImages = results.filter(item => 
              item.imageUrl && 
              !item.imageUrl.includes('undefined') &&
              (item.imageUrl.startsWith('http') || item.imageUrl.startsWith('/image/'))
            );
            
            log(`📸 Articles with valid images: ${articlesWithImages.length}/${results.length}`);
            log(`✍️ Articles with authors: ${articlesWithImages.filter(a => a.author).length}/${articlesWithImages.length}`);
            
            if (articlesWithImages.length > 0) {
              const montclarionArticles = articlesWithImages.filter(item => 
                item.sourceType === 'montclarion' || item.source?.toLowerCase().includes('montclar')
              );
              
              const otherArticles = articlesWithImages.filter(item => 
                item.sourceType !== 'montclarion' && !item.source?.toLowerCase().includes('montclar')
              );
              
              const prioritizedArticles = [...montclarionArticles, ...otherArticles].slice(0, 5);
              
              log(`🆕 Returning ${prioritizedArticles.length} MSU articles (${montclarionArticles.length} Montclarion, ${otherArticles.length} other)`);
              
              // ✅ RETURN WITH AUTHOR FIELD
              return prioritizedArticles.map(item => ({
                title: item.title,
                publisher: item.source || 'MSU News',
                summary: item.snippet || item.title,
                publishedAt: new Date(item.datePublished),
                imageUrl: item.imageUrl,
                url: item.url || 'https://www.montclair.edu/',
                author: item.author || null, // ✅ INCLUDE AUTHOR
                isNew: true
              }));
            } else {
              log('⚠️ No articles with valid images');
            }
          } else {
            log('⚠️ No results array found in response');
          }
        } else {
          log(`⚠️ MSU API HTTP error: ${response.status}`);
          const errorText = await response.text();
          log(`Error body: ${errorText}`);
        }
      } catch (msuError) {
        log(`❌ MSU API error: ${msuError.message}`);
        console.error('MSU fetch error:', msuError);
      }
      
      log('⚠️ Using MSU fallback news');
      return generateMSUFallbackNews();
    }
    
    // ============================================
    // REGULAR COMPANY NEWS (non-MSU)
    // ============================================
    if (!company.ticker) {
      log('⚠️ No ticker provided, using fallback');
      return generateFallbackNews();
    }
    
    log(`Fetching news for ${company.name} (${company.ticker})...`);
    
    const BACKEND_URL = 'https://us-central1-fir-7c9c9.cloudfunctions.net/fetchCompanyNews';
    
    const response = await fetch(`${BACKEND_URL}?ticker=${encodeURIComponent(company.ticker)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    log(`Response status: ${response.status}`);
    
    if (!response.ok) {
      log(`⚠️ HTTP error ${response.status}, using fallback`);
      return generateFallbackNews();
    }
    
    const data = await response.json();
    
    if (!data.success || !data.results || !Array.isArray(data.results) || data.results.length === 0) {
      log('⚠️ No news found in response, using fallback');
      return generateFallbackNews();
    }
    
    const recentNews = data.results.slice(0, 5).map(item => ({
      title: item.title,
      publisher: item.publisher,
      summary: item.summary || item.title,
      publishedAt: new Date(item.publishedAt),
      imageUrl: item.imageUrl || '/image/studycard10.png',
      url: item.url || '#',
      author: item.author || null, // ✅ INCLUDE AUTHOR (for future use)
      isNew: true
    }));
    
    log(`✅ Found ${recentNews.length} news items`);
    return recentNews;
    
  } catch (error) {
    log(`❌ News fetch error: ${error.message}`);
    console.error('Full fetch error:', error);
    return generateFallbackNews();
  }
};
// ============================================
// HELPER: Generate unique article ID
// ============================================
const generateArticleId = (title, source) => {
  // Normalize title (remove punctuation, extra spaces)
  const normalizedTitle = title
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 100); // First 100 chars to avoid oversized keys
  
  const normalizedSource = source
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .trim();
  
  return `${normalizedSource}::${normalizedTitle}`;
};


// ============================================
// HELPER: Get shown articles from localStorage
// ============================================
const getShownArticles = (ticker) => {
  try {
    const stored = localStorage.getItem(`shown_articles_${ticker}`);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (error) {
    log(`⚠️ Error reading shown articles: ${error.message}`);
  }
  return new Set();
};


// ============================================
// HELPER: Add article to shown list
// ============================================
const addShownArticle = (ticker, articleId, title, source) => {
  try {
    const shownArticles = getShownArticles(ticker);
    shownArticles.add(articleId);
    
    // ✅ KEEP HISTORY (max 50 articles to avoid oversizing localStorage)
    if (shownArticles.size > 50) {
      // Convert to array and remove oldest (first 20)
      const articlesArray = Array.from(shownArticles);
      const trimmed = articlesArray.slice(20);
      localStorage.setItem(`shown_articles_${ticker}`, JSON.stringify(trimmed));
    } else {
      localStorage.setItem(`shown_articles_${ticker}`, JSON.stringify(Array.from(shownArticles)));
    }
    
    log(`📌 Marked as shown: "${title.substring(0, 40)}..." (${source})`);
  } catch (error) {
    log(`⚠️ Error saving shown article: ${error.message}`);
  }
};


// ============================================
// HELPER: Clear article history (debugging/reset)
// ============================================
const clearArticleHistory = (ticker) => {
  try {
    localStorage.removeItem(`shown_articles_${ticker}`);
    log(`🗑️ Article history cleared for ${ticker}`);
  } catch (error) {
    log(`⚠️ Error clearing history: ${error.message}`);
  }
};


// ============================================
// HELPER: Get statistics on shown articles
// ============================================
const getArticleStats = (ticker) => {
  try {
    const shown = getShownArticles(ticker);
    return {
      totalShown: shown.size,
      limit: 50,
      percentFull: ((shown.size / 50) * 100).toFixed(1)
    };
  } catch (error) {
    return { totalShown: 0, limit: 50, percentFull: 0 };
  }
};
// ✅ ADD THIS NEW FUNCTION - MSU-SPECIFIC FALLBACK
const generateMSUFallbackNews = () => {
  const timeOfDay = getTimeOfDay();
  
  return [
    {
      title: 'Montclair State University Spring Semester Updates',
      publisher: 'MSU Campus News',
      summary: 'Latest updates on campus events, academic programs, and student services for the spring semester at Montclair State University.',
      publishedAt: new Date(),
      imageUrl: 'https://themontclarion.org/wp-content/uploads/2023/11/logo-2.png',
      url: 'https://www.montclair.edu/news/'
    },
    {
      title: 'Red Hawks Athletics: Recent Games and Upcoming Matches',
      publisher: 'MSU Athletics',
      summary: 'Catch up on the latest Red Hawks sports news, including recent victories and upcoming games across various athletic programs.',
      publishedAt: new Date(Date.now() - 3600000),
      imageUrl: '/image/studycard12.png',
      url: 'https://msuredwawks.com/'
    },
    {
      title: 'Campus Life: Student Organizations and Events This Week',
      publisher: 'MSU Student Life',
      summary: 'Discover what\'s happening on campus this week, from club meetings to special events and workshops for MSU students.',
      publishedAt: new Date(Date.now() - 7200000),
      imageUrl: '/image/studycard13.png',
      url: 'https://www.montclair.edu/student-life/'
    },
    {
      title: 'Academic Excellence: MSU Research Highlights',
      publisher: 'MSU Research',
      summary: 'Highlighting recent research breakthroughs and academic achievements by Montclair State University faculty and students.',
      publishedAt: new Date(Date.now() - 10800000),
      imageUrl: '/image/studycard14.png',
      url: 'https://www.montclair.edu/research/'
    },
    {
      title: 'Career Services: Job Fair and Internship Opportunities',
      publisher: 'MSU Career Development',
      summary: 'Upcoming career fairs, internship opportunities, and professional development resources available to MSU students.',
      publishedAt: new Date(Date.now() - 14400000),
      imageUrl: '/image/studycard5.png',
      url: 'https://www.montclair.edu/career-development/'
    }
  ];
};
// Helper function to clean company names
const cleanCompanyName = (name, forHeader = false) => {
  if (!name) return name;
  
  // ✅ Special handling for Montclair State University
  if (name === 'Montclair State University') {
    // Use SHORT version for header, FULL for audio/transcript
    return forHeader ? 'Montclair State Uni...' : 'Montclair State University';
  }
  
  // Remove common suffixes
  return name
    .replace(/\s+Inc\.?$/i, '')
    .replace(/\s+LLC\.?$/i, '')
    .replace(/\s+Corp\.?$/i, '')
    .replace(/\s+Corporation$/i, '')
    .replace(/\s+Limited$/i, '')
    .replace(/\s+Ltd\.?$/i, '')
    .replace(/\s+Co\.?$/i, '')
    .replace(/\s+Company$/i, '')
    .replace(/\s+Incorporated$/i, '')
    .replace(/\s+PLC\.?$/i, '')
    .trim();
};
const generateStationScript = async (newsItems, includeGuest = false, guestName = '') => {
  const timeOfDay = getTimeOfDay();

  // ✅ INCLUDE AUTHOR IN NEWS CONTEXT
  const newsContext = newsItems.map((item, idx) => {
    const authorLine = item.author ? `\nAuthor: ${item.author}` : '';
    return `${idx + 1}. ${item.title}\nPublisher: ${item.publisher}${authorLine}\nSummary: ${item.summary}`;
  }).join('\n\n');

  const guestInfo = includeGuest ? `

**GUEST FEATURE:** ${guestName} (interesting guest)
- NOT a journalist or expert - just an engaged listener
- Joins naturally around exchange 8-12 (not at start)
- Brings fresh perspective, curiosity, or personal interest
- Can ask questions, react, share observations
- Makes it feel like 3 friends discussing ${cleanCompanyName(company.name)} news

**GUEST ENTRANCE STYLES (pick one randomly):**

**STYLE 1: Casual Drop-In**
Guide: "This ${cleanCompanyName(company.name)} story is getting interesting"
Companion: "Yeah, I'm curious where this goes"
${guestName}: "Wait, are you talking about the ${cleanCompanyName(company.name)} thing"
Guide: "Yeah, what do you think"

**STYLE 2: Overhearing**
${guestName}: "Sorry, couldn't help overhearing—did you mention ${cleanCompanyName(company.name)}"
Companion: "Yeah, ${guestName}, you know about this"
${guestName}: "A bit. What's the story"

**GUEST PARTICIPATION:**
- ${guestName} contributes 25-35% after entering
- Natural reactions: "That's wild", "Oh interesting", "Wait, really"
- Can ask questions and disagree
- ❌ CANNOT reference personal experiences

**GUEST DEPARTURE:**
Guide or Companion: "Thanks for chiming in ${guestName}"
${guestName}: "Anytime, this was interesting"
` : '';

  // ✅ DETERMINE IF THIS IS MSU STATION
  const isMSUStation = company.ticker === 'MSU' || company.isLocal;

  const prompt = `Create a live news broadcast for ${cleanCompanyName(company.name)} (${company.ticker}).

**LISTENER:** ${userName}
**TIME OF DAY:** ${timeOfDay}
${guestInfo}

**LATEST NEWS:**
${newsContext}

**CRITICAL: YOU ARE SPEAKING TO ${userName} DIRECTLY**
- This is THEIR personal news briefing
- Say "you" when addressing them: "Here's what you need to know"
- Use their name SPARINGLY - maximum 2-3 times in the ENTIRE broadcast
- Most of the time just say "you" instead of repeating their name
- NOT reading a script - having a conversation WITH them
- Make it feel like ${includeGuest ? 'three analysts' : 'two analysts'} briefing THEM specifically

**HOSTS:**
- Guide: Main host
- Companion: Co-host
${includeGuest ? `- ${guestName}: Guest` : ''}

**🚨 CRITICAL - STORY TRANSITION MARKERS:**
Before moving to each NEW story, one host MUST use this EXACT pattern:

**MARKER FORMAT:**
"[STORY-${newsItems.map((_, idx) => idx + 1).join('|STORY-')}]"

**EXAMPLES:**
Guide: "[STORY-1] Alright, so here's what's happening with ${newsItems[0]?.publisher || 'the first story'}"
Companion: "[STORY-2] Moving on, ${newsItems[1]?.publisher || 'next up'} reported something interesting"
Guide: "[STORY-3] Next story from ${newsItems[2]?.publisher || 'another source'}"

**RULES FOR MARKERS:**
1. Use [STORY-1] for first story, [STORY-2] for second, etc.
2. Place marker at START of sentence (before the transition phrase)
3. Marker must be followed by natural transition
4. EVERY story must start with its marker
5. Only ONE marker per story
6. Marker appears in the FIRST line discussing that story

**✍️ CRITICAL - NATURAL AUTHOR ATTRIBUTION:**
${isMSUStation ? `
**FOR MSU STATION - SMART AUTHOR USAGE:**

**VARY YOUR ATTRIBUTION - Don't be repetitive:**
- Some stories: Mention author for credibility
- Some stories: Skip author, just dive into content
- Goal: Sound natural, not robotic

**WHEN TO MENTION AUTHORS (Mix it up):**

**Option 1 - Full Attribution (Use ~40% of time when author available):**
✅ "Sarah Johnson from The Montclarion reports that..."
✅ "According to Sarah Johnson at The Montclarion..."
✅ "Reporter Sarah Johnson covered this..."

**Option 2 - Just Reporter (Use ~30% of time when author available):**
✅ "Reporter Mike Chen covered this development..."
✅ "According to journalist Sarah Smith..."
✅ "Mike Chen reports that..."
(DON'T mention Yahoo/other publications if covering MSU - feels disconnected)

**Option 3 - Skip Author, Dive Right In (Use ~30% of time):**
✅ "The Montclarion broke this story about..."
✅ "So here's what's happening with campus dining..."
✅ "This Red Hawks development just came out..."

**NATURAL MIX EXAMPLES:**

Story 1 (WITH attribution):
Guide: "[STORY-1] Sarah Johnson from The Montclarion broke this story about campus expansion"
Companion: "What did she find"
Guide: "She's reporting MSU is adding three new residence halls"

Story 2 (NO attribution - direct):
Guide: "[STORY-2] So the Red Hawks just won the championship game"
Companion: "Wait, really"
Guide: "Yeah, they beat their rivals in overtime"

Story 3 (REPORTER only):
Guide: "[STORY-3] Reporter Mike Chen covered this dining development"
Companion: "What's the latest"
Guide: "He found they're extending hours until midnight"

**KEY PRINCIPLE:**
- Use author attribution to ADD CREDIBILITY when it feels natural
- Don't force it on EVERY story - that sounds robotic
- Mix it up based on story importance and flow
- For big/breaking news: More likely to mention reporter
- For routine updates: Can skip attribution and dive in
` : `
**FOR COMPANY NEWS - SMART AUTHOR USAGE:**

**VARY YOUR ATTRIBUTION - Don't be repetitive:**
- Some stories: Mention source/author for credibility
- Some stories: Skip attribution, just dive into content
- Goal: Sound natural, not robotic

**WHEN TO MENTION SOURCES (Mix it up):**

**Option 1 - Full Attribution (~40% of time):**
✅ "Reuters reports that..."
✅ "According to Bloomberg..."
✅ "CNBC's Sarah Johnson covered..."

**Option 2 - Partial (~30% of time):**
✅ "Financial analysts are noting..."
✅ "Market reports show..."
✅ "According to recent coverage..."

**Option 3 - Skip Attribution (~30% of time):**
✅ "So ${cleanCompanyName(company.name)} just announced..."
✅ "This earnings report just dropped..."
✅ "The stock jumped fifteen percent after..."

**NATURAL MIX EXAMPLES:**

Story 1 (WITH source):
Guide: "[STORY-1] Reuters broke this story about ${cleanCompanyName(company.name)}'s expansion"
Companion: "What are they reporting"
Guide: "They're saying revenue jumped twenty percent"

Story 2 (NO attribution - direct):
Guide: "[STORY-2] So ${cleanCompanyName(company.name)} just announced a major acquisition"
Companion: "Wait, who are they acquiring"
Guide: "They're buying a competitor for five billion dollars"

Story 3 (PARTIAL):
Companion: "[STORY-3] Market analysts are watching this development closely"
Guide: "What's happening"
Companion: "The stock hit an all-time high after the announcement"
`}

**CRITICAL - MAKE IT INTERESTING AND ENGAGING:**
✅ Show enthusiasm through WORDS, not punctuation
✅ Use vivid language: "massive", "significant", "notable", "remarkable"
✅ Create tension and interest: "Here's what's fascinating", "The interesting part is", "What's really catching attention"
✅ Vary sentence structure and energy
✅ React genuinely to the news: "Whoa", "That's huge", "This is significant", "This changes things"

❌ NEVER use exclamation marks (!)
❌ Don't be flat or monotone
❌ Avoid robotic delivery

**NO PERSONAL OPINIONS OR "I" STATEMENTS:**
❌ NEVER say: "I think", "I believe", "I feel", "I noticed", "I saw"
❌ NEVER say: "In my opinion", "From my perspective", "I find this"
✅ INSTEAD say: "This shows", "The data indicates", "Reports suggest", "Analysts note"

**FOCUS ON THE NEWS, NOT OPINIONS:**
✅ "The stock jumped fifteen percent after the announcement"
✅ "Analysts are watching this closely"
✅ "The market reacted strongly to this news"
✅ "This could signal a major shift"
✅ "Reports indicate significant changes ahead"

❌ "I think this is impressive"
❌ "I'm excited about this development"
❌ "I believe this will impact the sector"
❌ "I noticed something interesting here"

**USING "WE" - ONLY FOR SHOW OPERATIONS:**
✅ "We're covering..." (the show is covering)
✅ "We found that..." (collective reporting)
✅ "Let's look at..." (directing attention)
✅ "We should note..." (editorial observation)

❌ "We think this is great" (personal opinion)
❌ "We believe this matters" (personal belief)

**NUMBER PRONUNCIATION - CRITICAL:**
When speaking numbers, hosts must say them naturally:

✅ Say "you" when relevant:
- "This could affect your commute"
- "If you're heading downtown..."
- "You'll want to avoid..."
- "This is important for you to know"

❌ NEVER say:
- "for everyone"
- "for all of us"
- "for people"
- "this affects everyone"

Instead say:
- "for you"
- "this affects you"
- "if you're in the area"

✅ CORRECT NUMBER PRONUNCIATION:
- $10,000,000 → "ten million dollars"
- $5.2 billion → "five point two billion dollars"
- $1,500,000 → "one point five million dollars"
- 2.5% → "two point five percent"
- $485.67 → "four hundred eighty-five dollars"

**WRITE NUMBERS AS WORDS:**
- Always spell out numbers in dialogue
- TTS reads written numbers better: "ten million" not "$10M"

**YOUR SCRIPT STRUCTURE:**

**NEWS COVERAGE** (15-20 exchanges between Guide, Companion${includeGuest ? `, and ${guestName}` : ''})
   - Each story MUST start with [STORY-X] marker
   - VARY attribution style - don't mention author/source every time
   - Mix: Some with attribution, some without, some partial
   - Discuss news while addressing listener directly with "you"
   - Use ${userName}'s name only 2-3 times in entire broadcast
   - Focus on facts, data, and developments
   - React to information, not personal feelings
   - Reference specific numbers (spelled out as words)
   - Each turn: 20-35 words
   - MIX conversational styles - not every line needs "uhm"
   - Use background acknowledgments ("Mhm", "Yeah") naturally
   - BUILD interest and engagement through WORD CHOICE
   - Create narrative flow and tension
   ${includeGuest ? `- ${guestName} joins around exchange 8-12` : ''}
   ${includeGuest ? `- ${guestName} participates 25-35% after entering` : ''}

${includeGuest ? `**GUEST DEPARTURE** (1 exchange)` : ''}

**CRITICAL RULES:**
- MUST use [STORY-X] marker before each story
- VARY attribution - sometimes mention author/source, sometimes skip it entirely
- Don't be robotic - mix up your approach story by story
- NO "I think", "I believe", "I feel" - focus on the NEWS
- NO exclamation marks (!) - show enthusiasm through WORDS
- Speaking TO listener using "you", not repeating their name constantly
- Use "we" ONLY for show operations, NOT opinions
- Spell out all numbers as words for natural pronunciation
- Reference specific numbers and details from the news
- Factual analysis directed at the listener
- NO repetition - each exchange adds value
- Connect news items analytically
- DO NOT include greetings (that was handled already)
- Start directly with news analysis
- MIX delivery styles - variety is key
- Use fillers naturally, not robotically
- MAKE IT INTERESTING AND ENGAGING

Output ONLY the script:
Guide: [STORY-1] [text]
Companion: [text]
Guide: [STORY-2] [text]
${includeGuest ? `${guestName}: [text]` : ''}
(continue)`;

  try {
    const data = await SecureAPI.callAzureOpenAI(
      [
        { 
          role: 'system', 
          content: `You are a professional broadcast script writer creating a personalized news briefing for ${userName}. 

CRITICAL RULES:
1. MUST use [STORY-X] markers before each story transition
2. VARY attribution style - DON'T mention author/source on EVERY story (sounds robotic)
   - Story 1: Maybe mention author
   - Story 2: Maybe skip attribution, dive right in
   - Story 3: Maybe mention just "reporter" or source
   - MIX IT UP based on story importance and natural flow
3. Use ${userName}'s name MAXIMUM 2-3 times in the entire script
4. Use "you" and "your" throughout instead of repeating their name
5. Use "we" for collective discussion/analysis (default)
6. Spell out ALL numbers as words (ten million, five point two billion, etc.)
7. Make it conversational and direct - ${includeGuest ? 'three' : 'two'} analysts speaking TO the listener
8. Natural but professional - no slang like "bruh" or "wild"
9. NO exclamation marks (!) - show enthusiasm through vivid word choice
10. Be INTERESTING and ENGAGING - create narrative tension and use dynamic language
${includeGuest ? `11. ${guestName} joins naturally around exchange 8-12, NOT at the start` : ''}
12. CRITICAL: Don't force author attribution on every story - sounds generic and robotic`
        },
        { role: 'user', content: prompt }
      ],
      {
        temperature: 0.88,
        max_tokens: 1500
      }
    );

    return data.choices[0].message.content;
  } catch (error) {
    log(`❌ Script generation error: ${error.message}`);
    throw error;
  }
};
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  };

  // Preload audio
const preloadAudio = async (text, voice) => {
  const cacheKey = `${voice}-${text}`;
  if (audioQueueRef.current.has(cacheKey)) return;

  // ✅ CREATE SEPARATE ABORT CONTROLLER FOR THIS PRELOAD
  const preloadController = new AbortController();
  preloadAbortControllers.current.set(cacheKey, preloadController);

  try {
    // ✅ USE SECURE API - NO MORE EXPOSED KEY
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      1.1
    );

    const url = URL.createObjectURL(audioBlob);

    audioQueueRef.current.set(cacheKey, { url, text, voice, used: false });
    log(`✅ Preloaded: ${voice} - ${text.substring(0, 30)}...`);
  } catch (error) {
    if (error.name !== 'AbortError') {
      log(`⚠️ Preload error: ${error.message}`);
    }
  } finally {
    preloadAbortControllers.current.delete(cacheKey);
  }
};

const handleClose = () => {
  log('🔴 Closing station - COMPLETE SHUTDOWN');
  
  // ✅ CRITICAL: Stop everything immediately
  shouldStopRef.current = true;
  isPausedRef.current = true;
  
  // ✅ NUCLEAR CLEANUP (synchronous)
  if (currentAudioRef.current) {
    currentAudioRef.current.pause();
    currentAudioRef.current.currentTime = 0;
    currentAudioRef.current.volume = 0;
    currentAudioRef.current.src = '';
    currentAudioRef.current.load();
    currentAudioRef.current = null;
  }
  
  // ✅ Clear all state
  setIsPlaying(false);
  setIsPaused(false);
  setTranscript([]);
  setNewsCards([]);
  setNewsSources([]);
  setCurrentNewsIndex(0);
  setCurrentCardIndex(0);
  
  // ✅ Save session if needed
  if (scriptLinesRef.current.length > 0 && isPlaying) {
    const currentLineIndex = Math.floor(scriptLinesRef.current.length * 0.1);
    saveSessionData(currentLineIndex, currentNewsIndex, scriptLinesRef, totalNewsCount);
  }
  
  log('✅ Station closed - all audio stopped');
  
  onClose();
};
// 7️⃣ UPDATE CLEANUP ON UNMOUNT
useEffect(() => {
  return () => {
    log('🧹 Component unmounting - nuclear cleanup');
    shouldStopRef.current = true;
    isPausedRef.current = true;
    
    // Abort all preloads
    preloadAbortControllers.current.forEach(controller => {
      try {
        controller.abort();
      } catch (e) {}
    });
    preloadAbortControllers.current.clear();
    
    // Stop all tracked audio
    activeAudioElements.current.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
      } catch (e) {}
    });
    activeAudioElements.current.clear();
    
    // Clean up refs
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    // Clean up cache
    audioQueueRef.current.forEach(({ url }) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    });
    audioQueueRef.current.clear();
    isPreloadingRef.current.clear();
  };
}, []);

const togglePlayPause = async () => {
  
  if (isPaused) {
    // RESUME
    log('▶️ Resuming playback...');

    setIsPaused(false);
    isPausedRef.current = false;
    shouldStopRef.current = false;
    setIsPlaying(true);

    // ✅ SHOW SHIMMER WHILE AUDIO PREPARES
    setTranscript([{ voice: 'system', text: 'shimmer', timestamp: Date.now() }]);

    await new Promise(resolve => setTimeout(resolve, 200));

    if (scriptLinesRef.current && scriptLinesRef.current.length > 0) {
      const currentLineIndex = currentLineIndexRef.current || 0;

      log(`▶️ Resuming from line ${currentLineIndex}`);

      // ✅ Shimmer stays visible until resumeFromLine clears it
      await resumeFromLine(currentLineIndex);
    }

  } else if (isPlaying) {
    // PAUSE - INSTANT STOP (WITHOUT CLEARING TRANSCRIPT)
    log('⏸️ INSTANT PAUSE - Keeping transcript visible');

    setIsPaused(true);
    isPausedRef.current = true;
    shouldStopRef.current = true;

    log(`🔴 Stopping ${activeAudioElements.current.size} audio elements (keeping transcript)`);

    activeAudioElements.current.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
      } catch (e) {}
    });
    activeAudioElements.current.clear();

    currentAudioRef.current = null;

    if (intermissionAudioRef.current) {
      try {
        const audio = intermissionAudioRef.current;
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
        intermissionAudioRef.current = null;
      } catch (e) {
        intermissionAudioRef.current = null;
      }
    }

    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(({ url }) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      audioQueueRef.current.clear();
    }

    // ✅ DON'T CLEAR TRANSCRIPT - Keep it visible!
    await new Promise(resolve => setTimeout(resolve, 100));

    log('⏸️ Pause complete - transcript still visible');

    // ✅ Reset shouldStop so resume works
    shouldStopRef.current = false;

} else {
    // START
    log('▶️ Starting station...');
    shouldStopRef.current = false;
    isPausedRef.current = false;
    setIsPaused(false);

    runLiveStation();
  }
};

const forceStopAudio = () => {
  if (currentAudioRef.current) {
    try {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current.src = ''; // ✅ Clear source
      currentAudioRef.current.load(); // ✅ Force reload
      currentAudioRef.current = null;
      log('✅ Audio force stopped');
    } catch (e) {
      log(`⚠️ Error stopping audio: ${e.message}`);
      currentAudioRef.current = null;
    }
  }
};
const generateWelcomeBackMessage = async (userName, sessionData, currentNews) => {
  const timeElapsed = Math.floor((Date.now() - new Date(sessionData.lastPausedAt).getTime()) / 1000);
  const minutes = Math.floor(timeElapsed / 60);
  const hours = Math.floor(minutes / 60);
  
  // ✅ Casual time descriptions
  let timeAgoString;
  if (hours > 0) {
    timeAgoString = hours === 1 ? 'an hour ago' : 'a while ago';
  } else if (minutes > 5) {
    timeAgoString = 'a few minutes ago';
  } else if (minutes > 0) {
    timeAgoString = 'just a moment ago';
  } else {
    timeAgoString = 'just now';
  }
  
  // Check if it's a new day
  const wasYesterday = sessionData.date !== new Date().toDateString();
  
  // ✅ Check if they were in middle of listening (not finished)
  const progressPercent = (sessionData.lastLineIndex / sessionData.totalLines) * 100;
  const isPartialListen = progressPercent < 90 && !sessionData.isComplete;
  
  const timeOfDay = (() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  })();

  let prompt;
  
  if (wasYesterday) {
    // ✅ NEW DAY - Fresh news
    prompt = `You are the co-host at ${cleanCompanyName(company.name)}'s live station.

${userName} is returning the next day.

Create a warm, personalized greeting (2-3 sentences) that:
- Welcomes them back by name
- Acknowledges it's a new day with fresh news
- Sounds natural and friendly
- Time: ${timeOfDay}

Examples:
- "Hey ${userName}, welcome back to your ${timeOfDay} ${cleanCompanyName(company.name)} station! We've got a whole new set of updates for you today."
- "Morning ${userName}! You're back—and we've got fresh ${cleanCompanyName(company.name)} news to catch you up on today."

Output ONLY the greeting, nothing else.`;
  } else if (isPartialListen) {
    // ✅ SAME DAY - INCOMPLETE - Resuming mid-session
    const currentNewsTitle = currentNews?.[sessionData.currentNewsIndex]?.title?.substring(0, 40) || 'that story';
    const newsProgress = sessionData.currentNewsIndex + 1;
    
    prompt = `You are the co-host at ${cleanCompanyName(company.name)}'s live station.

${userName} paused ${timeAgoString} while you were discussing news story #${newsProgress}: "${currentNewsTitle}"

They were in the MIDDLE of listening - not finished. You were discussing this story.

Create a WARM, CONTINUING message (2-3 sentences) that:
- Acknowledges they stepped away but came back quickly
- References the SPECIFIC story they were on without repeating the title
- Picks up naturally WHERE YOU LEFT OFF - like you're continuing the conversation mid-sentence
- Use phrases like "we were just talking about", "as we were saying", "so like I was mentioning"
- Acts like hosts remember the discussion and continue naturally
- DON'T use their name after greeting
- Sounds like a natural radio conversation continuation

Examples (PICK UP MID-CONVERSATION):
- "Hey ${userName}, welcome back! So like we were saying, ${cleanCompanyName(company.name)}'s approach this year is really impressive. There's some solid moves they're making."
- "${userName}, you're back! As we were discussing, this new direction ${cleanCompanyName(company.name)} is taking is pretty significant. Let me break down what makes it so important."
- "Welcome back, ${userName}! So ${cleanCompanyName(company.name)} has been making these interesting developments. Here's what really stands out about it."

Output ONLY the message, nothing else. Make it feel like you're CONTINUING a conversation.`;
  } else {
    // ✅ SAME DAY - ALREADY FINISHED - Fresh restart
    prompt = `You are the co-host at ${cleanCompanyName(company.name)}'s live station.

${userName} already listened to today's full briefing but is tuning back in.

Create a warm, personalized message (1-2 sentences) that:
- Thanks them for coming back
- Indicates no new updates since they left
- Invites them to tune in later/tomorrow
- DON'T use their name
- Sounds conversational and friendly

Examples:
- "Thanks for tuning back in! Nothing new has come up since you left, but we'll have fresh updates for you tomorrow."
- "Welcome back! The news cycle's been quiet since your last update. Come back tomorrow for the latest."

Output ONLY the message, nothing else.`;
  }

  try {
const data = await SecureAPI.callAzureOpenAI(
  [
    { role: 'system', content: 'You are a warm, professional broadcast host creating personalized welcome-back messages. For mid-session resumes, pick up naturally where the conversation left off - like you\'re continuing mid-sentence.' },
    { role: 'user', content: prompt }
  ],
  {
    temperature: 0.9,
    max_tokens: 150
  }
);

const message = data.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
    return message;
  } catch (error) {
    log(`⚠️ Welcome back message generation failed`);
    if (wasYesterday) {
      return `Hey ${userName}, welcome back! We've got fresh ${cleanCompanyName(company.name)} news for you today.`;
    } else if (isPartialListen) {
      return `Welcome back, ${userName}! So as we were discussing, there's some interesting developments with ${cleanCompanyName(company.name)}.`;
    } else {
      return `Thanks for tuning back in! Nothing new since you left, but we'll have fresh updates tomorrow.`;
    }
  }
};
const handleWelcomeBack = async (sessionData, news) => {
  log('🔄 Handling welcome back...');
  
  // ✅ CHECK IF SESSION IS RECENT (within 30 minutes)
  const sessionAge = Date.now() - new Date(sessionData.lastPausedAt).getTime();
  const thirtyMinutes = 30 * 60 * 1000;
  
  if (sessionAge > thirtyMinutes) {
    log('⏰ Session is older than 30 minutes, starting fresh');
    await storageService.clearStationSession(company.ticker);
    await storageService.clearStationScript(company.ticker);
    
    // Start fresh from beginning
    shouldStopRef.current = false;
    setIsPlaying(true);
    setIsPaused(false);
    setTranscript([]);
    
    // Call runLiveStation to start fresh
    return runLiveStation();
  }
  
  log(`✅ Session is recent (${Math.floor(sessionAge / 1000)}s ago), resuming from saved position`);
  
  shouldStopRef.current = false;
  setIsPlaying(true);
  setIsPaused(false);
  setTranscript([]);

  log('Playing intro music for welcome back...');
  setTranscript([{ voice: 'system', text: `One sec, ${userName}...`, timestamp: Date.now() }]);
  
  // ✅ SHOW CARDS IMMEDIATELY (not skeleton, real cards)
  setIsLoadingCards(false);
  setIsSearchingComplete(true);
  
  await playIntroMusicWithFade(10);

  if (shouldStopRef.current) {
    setIsPlaying(false);
    return;
  }

  // ============================================
  // ✅ RESTORE NEWS CARDS FROM SESSION WITH SAVED CARD INDEX
  // ============================================
  if (sessionData.newsCards && sessionData.newsCards.length > 0) {
    const savedCardIndex = sessionData.currentCardIndex || 0;
    log(`✅ Restoring ${sessionData.newsCards.length} news cards, resuming at card ${savedCardIndex}`);
    
    setNewsCards(sessionData.newsCards.map((card, idx) => ({
      ...card,
      isActive: idx === savedCardIndex,
      isReady: idx <= savedCardIndex
    })));
    
    setCurrentCardIndex(savedCardIndex);
    setCurrentNewsIndex(savedCardIndex);
    setTotalNewsCount(sessionData.newsCards.length);
    
    // ✅ Scroll to saved card position
    setTimeout(() => {
      if (newsCardsRef.current?.children[savedCardIndex]) {
        newsCardsRef.current.children[savedCardIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 500);
  } else {
    // Fallback: fetch fresh news if cards not saved
    log('⚠️ No saved cards, using fetched news');
    const cards = convertNewsToCards(news);
    const savedCardIndex = sessionData.currentCardIndex || 0;
    
    setNewsCards(cards.map((card, idx) => ({
      ...card,
      isActive: idx === savedCardIndex,
      isReady: idx <= savedCardIndex
    })));
    
    setCurrentCardIndex(savedCardIndex);
    setCurrentNewsIndex(savedCardIndex);
    setTotalNewsCount(cards.length);
    
    // Scroll to saved card
    setTimeout(() => {
      if (newsCardsRef.current?.children[savedCardIndex]) {
        newsCardsRef.current.children[savedCardIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 500);
  }

  // ============================================
  // ✅ RESTORE GUEST STATE FROM SESSION
  // ============================================
  if (sessionData.hasGuest && sessionData.guestName) {
    setHasGuest(true);
    setGuestName(sessionData.guestName);
    log(`✅ Restored guest: ${sessionData.guestName}`);
  } else {
    setHasGuest(false);
    setGuestName('');
  }

  // ============================================
  // ✅ LOAD SAVED SCRIPT FROM FIREBASE
  // ============================================
  log('📜 Loading saved script from Firebase...');
  const savedScript = await storageService.getStationScript(company.ticker);

  if (!savedScript || !savedScript.script) {
    log('⚠️ No saved script found, generating fresh');
    
    // Generate fresh script
    const includeGuest = shouldIncludeGuest();
    const guest = includeGuest ? selectRandomGuest() : null;
    
    if (includeGuest && guest) {
      setHasGuest(true);
      setGuestName(guest.name);
    }
    
    const script = await generateStationScript(news, includeGuest, guest?.name || '');
    const lines = script.split('\n').filter(line => line.trim());
    scriptLinesRef.current = lines;
    
    // Build card map
    const cardMap = {};
    const linesPerCard = Math.floor(lines.length / newsCards.length);
    
    for (let i = 0; i < newsCards.length; i++) {
      const startLine = i * linesPerCard;
      const endLine = i === newsCards.length - 1 ? lines.length : (i + 1) * linesPerCard;
      cardMap[i] = { start: startLine, end: endLine };
    }
    
    setScriptToCardMap(cardMap);
    
    // Save for next time
    await storageService.saveStationScript(
      company.ticker,
      script,
      Object.values(cardMap),
      news.map(item => ({ title: item.title, publisher: item.publisher }))
    );
  } else {
    log('✅ Loaded saved script from Firebase');
    const lines = savedScript.script.split('\n').filter(line => line.trim());
    scriptLinesRef.current = lines;
    
    // ✅ REBUILD CARD MAP FROM SAVED SEGMENTS OR CALCULATE
    const cardMap = {};
    
    if (savedScript.segments && savedScript.segments.length > 0) {
      // Use saved segments
      savedScript.segments.forEach((segment, idx) => {
        cardMap[idx] = { start: segment.start, end: segment.end };
      });
      log('✅ Restored card map from saved segments');
    } else {
      // Recalculate if not saved
      const linesPerCard = Math.floor(lines.length / newsCards.length);
      for (let i = 0; i < newsCards.length; i++) {
        const startLine = i * linesPerCard;
        const endLine = i === newsCards.length - 1 ? lines.length : (i + 1) * linesPerCard;
        cardMap[i] = { start: startLine, end: endLine };
      }
      log('⚠️ Card map recalculated (not saved in session)');
    }
    
    setScriptToCardMap(cardMap);
  }

  // ============================================
  // ✅ GENERATE WELCOME BACK MESSAGE
  // ============================================
  const welcomeMessage = await generateWelcomeBackMessage(
    userName, 
    sessionData, 
    newsCards
  );

  setCurrentStatus('Broadcasting...');
  setTranscript([]);

  // ✅ PLAY WELCOME MESSAGE
  const hostVoice = Math.random() < 0.5 ? 'guide' : 'therapist';
  await playAudio(welcomeMessage, hostVoice);

  if (shouldStopRef.current) {
    setIsPlaying(false);
    return;
  }

  // ============================================
  // ✅ PRELOAD NEXT FEW SPEAKERS FROM RESUME POINT
  // ============================================
  log('📥 Preloading next speakers from resume point...');
  const lines = scriptLinesRef.current;
  const startLine = sessionData.lastLineIndex || 0;
  
  let preloadCount = 0;
  for (let i = startLine; i < Math.min(startLine + 10, lines.length) && preloadCount < 3; i++) {
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch) {
      const text = guideMatch[1].trim();
      log(`📥 Preloading: Guide`);
      void preloadAudio(text, 'guide');
      preloadCount++;
    } else if (companionMatch) {
      const text = companionMatch[1].trim();
      log(`📥 Preloading: Therapist`);
      void preloadAudio(text, 'therapist');
      preloadCount++;
    } else if (guestMatch) {
      const text = guestMatch[1].trim();
      const guestVoice = guestName === 'Kenny' ? 'guest1' : 'guest2';
      log(`📥 Preloading: ${guestName}`);
      void preloadAudio(text, guestVoice);
      preloadCount++;
    }
  }

  // ✅ WAIT FOR PRELOADS
  await new Promise(resolve => setTimeout(resolve, 500));

  if (shouldStopRef.current) {
    setIsPlaying(false);
    return;
  }

  // ============================================
  // ✅ RESUME FROM SAVED LINE saveSessionData = (lineI
  // ============================================
  log(`▶️ Resuming from saved line ${sessionData.lastLineIndex}`);
  await resumeFromLine(sessionData.lastLineIndex);
};
const resumeFromLine = async (startLineIndex) => {
  log(`▶️ Resuming from line ${startLineIndex}`);
  const lines = scriptLinesRef.current;
  
  if (!lines || lines.length === 0) {
    log('⚠️ No script lines available');
    return;
  }
  
  // ✅ DETECT STARTING CARD (fallback)
  let currentCard = 0;
  for (let cardIdx = 0; cardIdx < newsCards.length; cardIdx++) {
    const range = scriptToCardMap[cardIdx];
    if (range && startLineIndex >= range.start && startLineIndex < range.end) {
      currentCard = cardIdx;
      break;
    }
  }
  
  log(`🎯 Resuming at Card ${currentCard} (line ${startLineIndex})`);
  
  // ✅ INSTANT CARD SYNC
  setCurrentCardIndex(currentCard);
  setCurrentNewsIndex(currentCard);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === currentCard,
    isReady: idx <= currentCard
  })));
  
  if (newsCardsRef.current?.children[currentCard]) {
    newsCardsRef.current.children[currentCard].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }
  
  currentLineIndexRef.current = startLineIndex;
  
  for (let i = startLineIndex; i < lines.length; i++) {
    currentLineIndexRef.current = i;
    
    if (shouldStopRef.current) break;
    
    while (isPausedRef.current && !shouldStopRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    if (shouldStopRef.current) break;
    
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch || companionMatch || guestMatch) {
      let voice, text;
      
      if (guideMatch) {
        voice = 'guide';
        text = guideMatch[1].trim();
      } else if (companionMatch) {
        voice = 'therapist';
        text = companionMatch[1].trim();
      } else if (guestMatch) {
        voice = guestName === 'Kenny' ? 'guest1' : 'guest2';
        text = guestMatch[1].trim();
      }

      // ✅ DETECT STORY MARKER
      const detectedStory = detectStoryMarker(text);
      
if (detectedStory !== null && detectedStory !== currentCard) {
  log(`🎯 MARKER DETECTED: [STORY-${detectedStory + 1}] → Card ${detectedStory}`);

  // ✅ Block marker from dragging card backward if user jumped ahead
  const userIsAheadOfMarker = hasTransitionedToCard.current.size > 0 &&
    currentCardIndex > detectedStory;

  if (userIsAheadOfMarker) {
    log(`⛔ BLOCKED marker rollback in resume: user at card ${currentCardIndex}, ignoring marker for card ${detectedStory}`);
    // still strip marker from text and play
    const cleanText = text.replace(/\[STORY-\d+\]\s*/g, '');
    await playAudio(cleanText, voice);
    if (shouldStopRef.current) break;
    continue;
  }

  currentCard = detectedStory;
  setCurrentCardIndex(detectedStory);
  setCurrentNewsIndex(detectedStory);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === detectedStory,
    isReady: idx <= detectedStory
  })));
  if (newsCardsRef.current?.children[detectedStory]) {
    newsCardsRef.current.children[detectedStory].scrollIntoView({
      behavior: 'smooth', block: 'nearest', inline: 'center'
    });
  }
}
      // ✅ REMOVE MARKER FROM TEXT
      const cleanText = text.replace(/\[STORY-\d+\]\s*/g, '');

      await playAudio(cleanText, voice);
      
      if (shouldStopRef.current) break;
    }
  }
  
  log('✅ Resume playback complete');
};
// Helper function to play audio for specific duration
const playAudioForDuration = (audioPath, duration, volume = 0.4) => {
  return new Promise((resolve) => {
    const audio = new Audio(audioPath);
    audio.volume = volume;
    currentAudioRef.current = audio;
    
    const timeout = setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
      currentAudioRef.current = null;
      log(`✅ Audio stopped after ${duration}s`);
      resolve();
    }, duration * 1000);
    
    audio.onended = () => {
      clearTimeout(timeout);
      currentAudioRef.current = null;
      log('✅ Audio ended naturally');
      resolve();
    };
    
    audio.onerror = (e) => {
      clearTimeout(timeout);
      currentAudioRef.current = null;
      log(`⚠️ Audio error, continuing...`);
      resolve();
    };
    
    audio.play().catch((e) => {
      clearTimeout(timeout);
      log(`⚠️ Audio play failed, continuing...`);
      resolve();
    });
    
    // Handle stop request
    if (shouldStopRef.current) {
      clearTimeout(timeout);
      audio.pause();
      currentAudioRef.current = null;
      resolve();
    }
  });
};

const stopIntermissionMusic = async () => {
  try {
    log('🛑 Stopping intermission music before hosts speak...');
    
    // Stop intermission ref
    if (intermissionAudioRef.current) {
      intermissionAudioRef.current.pause();
      intermissionAudioRef.current.currentTime = 0;
      intermissionAudioRef.current.volume = 0;
      intermissionAudioRef.current.src = '';
      intermissionAudioRef.current.load();
      intermissionAudioRef.current = null;
    }
    
    // Stop current audio if it's looped (intermission music)
    if (currentAudioRef.current && currentAudioRef.current.loop) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current.volume = 0;
      currentAudioRef.current.src = '';
      currentAudioRef.current.load();
      currentAudioRef.current = null;
    }
    
    // Remove looped audio from active elements tracking
    activeAudioElements.current.forEach(audio => {
      if (audio.loop) {
        try {
          audio.pause();
          audio.volume = 0;
          activeAudioElements.current.delete(audio);
        } catch (e) {}
      }
    });
    
    log('✅ Intermission music stopped - hosts ready to speak');
  } catch (e) {
    log(`⚠️ Error stopping intermission: ${e.message}`);
  }
};

  // Run live station
// ============================================
// COMPLETE FIXED runLiveStation FUNCTION
// ============================================
// Fixes:
// 1. News cards shimmer DURING intermission (not intro)
// 2. 200ms natural gap after intermission before host speaks
// 3. Safe pause/play handling (no conflict errors)

const runLiveStation = async () => {
  log('=== STARTING LIVE STATION ===');
  log(`Company: ${cleanCompanyName(company.name)} (${company.ticker})`);
  log(`User: ${userName}`);
  log('===========================');
  
  shouldStopRef.current = false;
  setIsPlaying(true);
  setIsPaused(false);
  setTranscript([]);
  setCurrentStatus('loading...');

  try {
    // ✅ CHECK FOR EXISTING SESSION FROM FIREBASE
    const existingSession = await storageService.getStationSession(company.ticker);
    const isSameDay = existingSession && existingSession.date === new Date().toDateString();

    if (isSameDay) {
      log('🔄 Same-day resume detected from Firebase');
      
      const news = await fetchCompanyNews();
      await handleWelcomeBack(existingSession, news);
      return;
    }
    
    if (existingSession && !isSameDay) {
      await storageService.clearStationSession(company.ticker);
      log('🗑️ Old session cleared from Firebase, starting fresh');
    }

    log('🆕 Starting fresh session');

    log('Starting intro music...');
    setTranscript([{ voice: 'system', text: `One sec, ${userName}...`, timestamp: Date.now() }]);
    
    // ✅ INTRO STARTS: No loading state yet
    setIsLoadingCards(false);
    setIsSearchingComplete(false);
    
    // ============================================
    // ✅ PARALLEL: Start all 3 tasks at once
    // ============================================
    const introMusicPromise = playIntroMusicWithFade(10);
    const newsPromise = fetchCompanyNews();
    
    // ============================================
    // ✅ AGGRESSIVE: GENERATE INTRO AUDIO WHILE MUSIC PLAYS
    // ============================================
    const timeOfDay = getTimeOfDay();
    const hostStarts = Math.random() < 0.5 ? 'guide' : 'therapist';

    log('🎙️ Generating intro audio in parallel (while music plays)...');

    const introPrompt = `You are the main host of ${cleanCompanyName(company.name)}'s live station.

**LISTENER:** ${userName}
**TIME OF DAY:** ${timeOfDay}
**COMPANY:** ${cleanCompanyName(company.name)}

Create a warm, personalized greeting (2-3 sentences) that:
- Greets ${userName} by name naturally
- Acknowledges the time of day smoothly
- Mentions ${cleanCompanyName(company.name)} casually
- Ends with "sit back and relax as we get those updates for you"
- Sounds genuine and conversational

❌ NEVER use exclamation marks (!)
❌ Don't be flat or monotone
❌ Avoid robotic delivery

Output ONLY the greeting text, nothing else.`;

    const introAudioPromise = (async () => {
      let introText;
      try {
        const introData = await SecureAPI.callAzureOpenAI(
          [
            { 
              role: 'system', 
              content: 'You are a warm, genuine broadcast host. Create natural, personalized greetings.'
            },
            { role: 'user', content: introPrompt }
          ],
          {
            temperature: 0.95,
            max_tokens: 120
          }
        );

        introText = introData.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
        log(`✅ Generated intro text: "${introText}"`);
      } catch (error) {
        log(`⚠️ Intro generation failed, using fallback`);
        if (timeOfDay === 'morning') {
          introText = `Good morning ${userName}. Perfect time to catch up on ${cleanCompanyName(company.name)} before your day gets rolling. Just sit back and relax - we'll get you caught up.`;
        } else if (timeOfDay === 'afternoon') {
          introText = `Hey ${userName}, welcome back. We know you're squeezing this in during your afternoon, so let's make it worth it. Sit back and relax as we pull up the latest on ${cleanCompanyName(company.name)}.`;
        } else {
          introText = `Hey ${userName}, glad you're winding down the day with us. It's late, so this is just a quick, clean pass over what actually matters with ${cleanCompanyName(company.name)} before you call it a night. Sit back and relax as we get those updates for you.`;
        }
      }

      log(`🎙️ Generating TTS for intro (while music plays - ~2-3 seconds)...`);
      try {
        const audioBlob = await SecureAPI.callHumeTTSSecure(
          introText,
          VOICE_CONFIG[hostStarts],
          playbackSpeedRef.current
        );

        const trimmedBlob = await trimSilenceAggressive(audioBlob);
        const audioUrl = URL.createObjectURL(trimmedBlob);
        
        const cacheKey = `${hostStarts}-${introText}`;
        audioQueueRef.current.set(cacheKey, { url: audioUrl, text: introText, voice: hostStarts, used: false });
        
        log(`✅ INTRO AUDIO READY AND CACHED`);
        return { ready: true, url: audioUrl, text: introText, voice: hostStarts };
      } catch (error) {
        log(`⚠️ Intro TTS generation failed: ${error.message}`);
        return { ready: false };
      }
    })();

    // ============================================
    // ✅ WAIT FOR INTRO MUSIC TO FINISH
    // ============================================
    log('Waiting for intro music to finish...');
    await introMusicPromise;

    if (shouldStopRef.current) {
      setIsPlaying(false);
      return;
    }

    // ✅ FETCH NEWS DATA
    const newsData = await newsPromise;
    
    if (newsData.length === 0) {
      log('⚠️ No news available');
      
      const noNewsPrompt = `You are the main host of ${cleanCompanyName(company.name)}'s live station. No recent news available.

Create apologetic message (1-2 sentences) for ${userName}.

Output ONLY the message.`;

      let noNewsText;
      try {
        const noNewsData = await SecureAPI.callAzureOpenAI(
          [
            { role: 'system', content: 'You are a warm broadcast host.' },
            { role: 'user', content: noNewsPrompt }
          ],
          {
            temperature: 0.9,
            max_tokens: 80
          }
        );

        noNewsText = noNewsData.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
      } catch (error) {
        noNewsText = `Unfortunately, we couldn't gather recent updates right now. Please check back later.`;
      }
      
      setCurrentStatus('Broadcasting...');
      setTranscript([]);
      
      await playAudio(noNewsText, 'guide');
      showFarewellCard();
      return;
    }

    // ✅ CREATE NEWS CARDS (but DON'T show yet - wait for intermission)
    const cards = newsData.map((article, idx) => ({
      id: idx,
      title: article.title.split(' ').slice(0, 4).join(' '),
      fullTitle: article.title,
      image: article.imageUrl || '/image/studycard10.png',
      source: article.publisher,
      url: article.url,
      time: new Date(article.publishedAt).toLocaleDateString(),
      isReady: false,
      isActive: false,
      snippet: article.summary
    }));

    // ✅ SETUP GUEST
    const includeGuest = shouldIncludeGuest();
    const guest = includeGuest ? selectRandomGuest() : null;
    
    if (includeGuest && guest) {
      setHasGuest(true);
      setGuestName(guest.name);
      log(`🎙️ Guest ${guest.name} will join`);
    } else {
      setHasGuest(false);
      setGuestName('');
    }

    const sourcesSummary = newsData.map((item, idx) => ({
      id: idx,
      title: item.title,
      shortTitle: generateShortTitle(item.title),
      publisher: item.publisher,
      isReady: false,
      isGenerated: false,
      isActive: false
    }));
    setNewsSources(sourcesSummary);

    // ✅ WAIT FOR INTRO AUDIO TO BE READY
    log('⏳ Waiting for intro audio to be ready...');
    const introAudioStatus = await introAudioPromise;

    if (shouldStopRef.current) {
      setIsPlaying(false);
      return;
    }

    // ✅ IMMEDIATELY PLAY INTRO AUDIO
    log(`🎤 Playing intro audio IMMEDIATELY (already cached)...`);
    setTranscript([]);
    const introPlayPromise = playAudio(introAudioStatus.text, hostStarts);

    log('⏳ Intro audio is playing...');
    await introPlayPromise;
    
log('✅ Intro complete - starting intermission');

// ✅ NEW: START INTERMISSION FIRST (no loading state yet)
log('🎵 Starting 5-second intermission music...');

const intermissionPromise = playIntermissionMusicWithFade(); // 5 seconds
const scriptPromise = generateStationScript(newsData, includeGuest, guest?.name || ''); // ~3-4 seconds

// ✅ SHOW LOADING STATE ONLY AFTER 1 SECOND (during intermission music)
setTimeout(() => {
  if (!shouldStopRef.current) {
    setTranscript([{ voice: 'system', text: 'Searching news...', timestamp: Date.now() }]);
    setCurrentStatus('Building your briefing');
    setIsLoadingCards(true);
    setIsSearchingComplete(false);
  }
}, 1000); // Wait 1 second before showing loading

    log('📰 Fetching and processing news...');

    // ============================================
    // ✅ WAIT FOR BOTH SCRIPT AND INTERMISSION
    // ============================================
    log('⏳ Waiting for script generation and intermission music...');

    const [script] = await Promise.all([
      scriptPromise,
      intermissionPromise // Ensures we don't start speaking until music fades
    ]);

    if (shouldStopRef.current) {
      setIsPlaying(false);
      return;
    }

    // ============================================
    // ✅ PROCESS SCRIPT ULTRA-FAST
    // ============================================
    const lines = script.split('\n').filter(line => line.trim());
    scriptLinesRef.current = lines;

    const estimatedLinesPerNews = Math.ceil(lines.length / totalNewsCount);
    newsSegmentsRef.current = [];

    for (let i = 0; i < totalNewsCount; i++) {
      const startIdx = i * estimatedLinesPerNews;
      const endIdx = Math.min((i + 1) * estimatedLinesPerNews, lines.length);
      newsSegmentsRef.current.push({ start: startIdx, end: endIdx });
    }

    await storageService.saveStationScript(
      company.ticker,
      script,
      newsSegmentsRef.current,
      newsData.map(item => ({ title: item.title, publisher: item.publisher }))
    );

    // ============================================
    // ✅ AGGRESSIVE: PRE-GENERATE FIRST 3 HOST AUDIOS
    // ============================================
    log('🚀 ULTRA-AGGRESSIVE: Generating first 3 host audios NOW...');

    const firstThreePromises = [];
    let foundCount = 0;

    for (let i = 0; i < lines.length && foundCount < 3; i++) {
      const line = lines[i];
      const guideMatch = line.match(/Guide:\s*(.+)/i);
      const companionMatch = line.match(/Companion:\s*(.+)/i);
      const guestMatch = hasGuest && guestName
        ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
        : null;

      if (guideMatch || companionMatch || guestMatch) {
        const voice = guideMatch ? 'guide' : (companionMatch ? 'therapist' : (guestName === 'Kenny' ? 'guest1' : 'guest2'));
        const text = (guideMatch || companionMatch || guestMatch)[1].trim().replace(/\[STORY-\d+\]\s*/g, '');
        
        log(`📥 Pre-generating [${foundCount + 1}/3]: ${voice}`);
        
        firstThreePromises.push(
          (async () => {
            try {
              const audioBlob = await SecureAPI.callHumeTTSSecure(
                text,
                VOICE_CONFIG[voice],
                playbackSpeedRef.current
              );
              const trimmedBlob = await trimSilenceAggressive(audioBlob);
              const audioUrl = URL.createObjectURL(trimmedBlob);
              
              const cacheKey = `${voice}-${text}`;
              audioQueueRef.current.set(cacheKey, { url: audioUrl, text, voice, used: false });
              
              log(`✅ CACHED [${foundCount + 1}/3]: ${voice}`);
              return { ready: true };
            } catch (error) {
              log(`⚠️ Pre-gen failed: ${error.message}`);
              return { ready: false };
            }
          })()
        );
        
        foundCount++;
      }
    }

    // ✅ WAIT FOR ALL 3 TO BE READY
    await Promise.all(firstThreePromises);

    // ✅ IMMEDIATELY SHOW REAL CARDS (no delay)
    log('✅ Showing real news cards');
    setNewsCards(cards);
    setTotalNewsCount(newsData.length);
    setIsLoadingCards(false);
    setIsSearchingComplete(true);

    // ✅ CLEAR STATUS IMMEDIATELY
    setTranscript([]);
    setCurrentStatus('');
    setLoadingMessage('');

    // ✅ ZERO-DELAY START (host speaks immediately after intermission fade)
    log('⚡ INSTANT PLAYBACK - Host speaking NOW');
    setCurrentStatus('Broadcasting...');

    // ✅ START SCRIPT IMMEDIATELY (first 3 audios already cached!)
    log('🎙️ Starting script playback...');
    await playScript(script);    
    
if (shouldStopRef.current) {
  setIsPlaying(false);
  return;
}

// ✅ playScript now handles wrap-up — just do final farewell
if (!shouldStopRef.current) {
  localStorage.setItem(`station_${company.ticker}_lastListen`, new Date().toDateString());
  setHasListenedToday(true);
  await storageService.clearStationSession(company.ticker);
  log('✅ Station complete');
  showFarewellCard();
}

    // ============================================
    // GENERATE CLOSING
    // ============================================
    log('Generating closing...');
    
    const closingPrompt = `You are ${hostStarts === 'guide' ? 'the main host' : 'the co-host'} wrapping up ${cleanCompanyName(company.name)}'s live station.

Create brief closing (1-2 sentences) that:
- Wraps up naturally
- Invites them to tune in tomorrow
- DON'T use their name
- Varied structure

Output ONLY the closing.`;
    
    let closingText;
    try {
      const closingData = await SecureAPI.callAzureOpenAI(
        [
          { role: 'system', content: 'You are a warm broadcast host closing a show. Create varied closings.' },
          { role: 'user', content: closingPrompt }
        ],
        {
          temperature: 0.9,
          max_tokens: 80
        }
      );

      closingText = closingData.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
      log(`Generated closing: "${closingText}"`);
    } catch (error) {
      log(`⚠️ Closing generation failed, using fallback`);
      closingText = `That's all for today's ${cleanCompanyName(company.name)} updates. Tune in tomorrow for more news.`;
    }

    await playAudio(closingText, hostStarts);

    localStorage.setItem(`station_${company.ticker}_lastListen`, new Date().toDateString());
    setHasListenedToday(true);

    await storageService.clearStationSession(company.ticker);

    log('✅ Station complete');
    showFarewellCard();

  } catch (error) {
    log(`❌ Station error: ${error?.message || error}`);
    console.error('Full error:', error);
    
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    
    alert(`Failed to run live station. Please try again.`);
    setIsPlaying(false);
  }
};
const handleAlreadyListened = async () => {
  shouldStopRef.current = false;
  setIsPlaying(true);
  
  // Play first 3 seconds of intro music
await playIntroMusicWithFade(10);

  if (shouldStopRef.current) {
    setIsPlaying(false);
    return;
  }

  // ✅ Generate dynamic "already listened" message
const alreadyListenedPrompt = `You are the host of ${cleanCompanyName(company.name)}'s live station.

The listener has tuned back in again, but they've already heard today's ${cleanCompanyName(company.name)} news.

Create a warm, friendly message (2-3 sentences) that:
- Acknowledges they're back (use "you" - don't repeat their name)
- Explains they've covered today's news already
- Invites them to return tomorrow for fresh updates
- Sounds natural and appreciative

Output ONLY the message text, nothing else.`;
  let message;
  try {
const data = await SecureAPI.callAzureOpenAI(
  [
    { role: 'system', content: 'You are a warm, professional broadcast host. Create natural, varied messages.' },
    { role: 'user', content: alreadyListenedPrompt }
  ],
  {
    temperature: 0.9,
    max_tokens: 100
  }
);

message = data.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
  } catch (error) {
    log(`⚠️ Already listened message generation failed, using fallback`);
    message = `Hey ${userName}, we see you tuned back in again. For now, we've pretty much covered ${cleanCompanyName(company.name)}'s latest news for today. You can tune in tomorrow to get a fresh update about ${cleanCompanyName(company.name)}'s stock.`;
  }

  await playAudio(message, 'guide');

  if (shouldStopRef.current) {
    setIsPlaying(false);
    return;
  }

  // ✅ Generate dynamic farewell
  const timeOfDay = getTimeOfDay();
const farewellPrompt = `You are the host signing off.

TIME: ${timeOfDay}

Create a brief, warm farewell (1 sentence) appropriate for ${timeOfDay} that:
- Wishes the listener well (use "you", not their name)
- Is time-appropriate (morning/afternoon/evening)
- Sounds natural and friendly

Examples:
- "Have a great rest of your day."
- "Enjoy your evening."
- "Have a wonderful afternoon."

Output ONLY the farewell text, nothing else.`;
  let farewell;
  try {
const data = await SecureAPI.callAzureOpenAI(
  [
    { role: 'system', content: 'You are a warm, professional broadcast host. Create natural farewells.' },
    { role: 'user', content: farewellPrompt }
  ],
  {
    temperature: 0.9,
    max_tokens: 40
  }
);

farewell = data.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
  } catch (error) {
    const farewellDefault = timeOfDay === 'morning' ? 'Have a wonderful day' :
                     timeOfDay === 'afternoon' ? 'Have a lovely afternoon' : 'Have a peaceful evening';
    farewell = `${farewellDefault}, ${userName}.`;
  }
  
  await playAudio(farewell, 'guide');

  showFarewellCard();
};

  const showFarewellCard = () => {
    setShowFarewell(true);
    setIsPlaying(false);
    
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
  };

  const changeSpeed = () => {
    const speeds = [1.0, 1.15, 1.25, 1.5];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
  };

  const skipForward = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.currentTime = Math.min(
        currentAudioRef.current.duration,
        currentAudioRef.current.currentTime + 15
      );
    }
  };

  const skipBackward = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.currentTime = Math.max(
        0,
        currentAudioRef.current.currentTime - 15
      );
    }
  };

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ============================================
// SHIMMER LOADING COMPONENT
// ============================================
const ShimmerLine = () => (
  <div style={{
    marginBottom: '20px',
    textAlign: 'left',
    animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  }}>
    <div style={{
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#ffffff',
      marginTop: '8px',
      flexShrink: 0,
      animation: 'pulseGlow 1.5s infinite'
    }} />
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      <div style={{
        height: '16px',
        borderRadius: '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        width: '90%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          animation: 'shimmer 1.5s infinite',
          backgroundSize: '200% 100%'
        }} />
      </div>
      <div style={{
        height: '16px',
        borderRadius: '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        width: '75%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          animation: 'shimmer 1.5s infinite 0.2s',
          backgroundSize: '200% 100%'
        }} />
      </div>
      <div style={{
        height: '16px',
        borderRadius: '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        width: '85%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          animation: 'shimmer 1.5s infinite 0.4s',
          backgroundSize: '200% 100%'
        }} />
      </div>
    </div>
  </div>
);
return (
<div style={{
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'flex-end',
  animation: 'fadeIn 0.25s ease'
}}>
  <div style={{
    width: '100%',
    height: '90vh',
    backgroundColor: '#1a1a1a',
    borderRadius: '28px 28px 0 0',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderTop: '2px solid #8d8d8d',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column'
  }}>
    {/* Background Gradient Layer 1 */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${COMPANY_LOGOS[company.ticker]?.color || company.color}40 0%, ${hexToRgba(COMPANY_LOGOS[company.ticker]?.color || company.color, 0.1)} 100%)`,
      opacity: 0.25,
      zIndex: 0
    }} />

    {/* Background Gradient Layer 2 - Blurred */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${COMPANY_LOGOS[company.ticker]?.color || company.color}40 0%, ${hexToRgba(COMPANY_LOGOS[company.ticker]?.color || company.color, 0.1)} 100%)`,
      filter: 'blur(100px) saturate(1.2)',
      transform: 'scale(1.1)',
      zIndex: 0,
      opacity: 0.4
    }} />

    {/* Dark Overlay */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))',
      zIndex: 1
    }} />

    {/* Header */}
    <div style={{
      padding: '24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative',
      zIndex: 2,
      background: `linear-gradient(90deg, rgba(0,0,0,0.4), ${COMPANY_LOGOS[company.ticker]?.color || company.color}05)`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
        {/* Company Logo */}
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `${COMPANY_LOGOS[company.ticker]?.color || company.color}20`,
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px solid ${COMPANY_LOGOS[company.ticker]?.color || company.color}60`,
          padding: '6px',
          flexShrink: 0,
          boxShadow: `0 4px 12px ${COMPANY_LOGOS[company.ticker]?.color || company.color}30`
        }}>
          <CompanyLogo 
            ticker={company.ticker} 
            name={company.name}
            size="36px"
            showInitial={true}
          />
        </div>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#fff',
            textAlign: 'left',
            marginBottom: '2px',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
{cleanCompanyName(company.name, true)}'s Live Station
          </div>
          <div style={{
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'left',
            background: currentStatus 
              ? 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.4) 100%)'
              : 'none',
            backgroundSize: currentStatus ? '200% 100%' : 'auto',
            backgroundClip: currentStatus ? 'text' : 'initial',
            WebkitBackgroundClip: currentStatus ? 'text' : 'initial',
            WebkitTextFillColor: currentStatus ? 'transparent' : 'rgba(255, 255, 255, 0.7)',
            animation: currentStatus ? 'textShimmer 3s linear infinite' : 'none'
          }}>
            {currentStatus || 'Real-time updates'}
          </div>
        </div>
      </div>
      
      <button
        onClick={handleClose}  
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          flexShrink: 0,
          marginLeft: '12px'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
      >
        <X size={18} color="#fff" />
      </button>
    </div>

{/* Content Area - Scrollable */}
    <div style={{
      flex: 1,
      position: 'relative',
      zIndex: 2,
      overflowY: 'auto',
      padding: '24px',
      paddingBottom: '280px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Initial State - ONLY show when never started */}
      {!isPlaying && !isPaused && transcript.length === 0 && (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeInUp 0.6s ease'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
            }}>
              Tap the play button below to begin!
            </h3>
            <p style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: '1.5'
            }}>
              Latest {company.name} news & updates
            </p>
          </div>
        </div>
      )}

      {/* Loading Message - ONLY show when loading (not paused) */}
      {isPlaying && !isPaused && transcript.length === 0 && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            textAlign: 'left',
            animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#ffffff',
              marginTop: '8px',
              flexShrink: 0,
              animation: 'pulseGlow 1.5s infinite'
            }} />
            <div style={{
              fontSize: '20px',
              fontWeight: '500',
              color: '#ffffff',
              lineHeight: '1.6',
              flex: 1,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.4) 100%)',
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'textShimmer 3s linear infinite'
            }}>
              {loadingMessage}
            </div>
          </div>
        </div>
      )}

{/* Paused State - Click Play to Resume */}
      {isPaused && isPlaying && !isNavigating && (
                <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeIn 0.6s ease'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `${COMPANY_LOGOS[company.ticker]?.color || company.color}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <Pause size={40} color={COMPANY_LOGOS[company.ticker]?.color || company.color} />
            </div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '8px',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
            }}>
              Paused
            </h3>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: '1.5'
            }}>
              Click play to resume
            </p>
          </div>
        </div>
      )}

      {/* Transcript - ONLY show when playing and NOT paused */}
{transcript.length > 0 && (isNavigating || (!isPaused && isPlaying)) && (
    <div style={{ marginBottom: '32px' }}>
    {transcript.slice(-1).map((item) => {
      const isSearching = item.voice === 'system' || item.text.includes('Searching') || item.text.includes('One sec');
      const isShimmer = item.text === 'shimmer';
      
      // ✅ SHOW SHIMMER LOADING
      if (isShimmer) {
        return <ShimmerLine key={item.timestamp} />;
      }
      
      return (
        <div key={item.timestamp} style={{
          marginBottom: '20px',
          textAlign: 'left',
          animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#ffffff',
            marginTop: '8px',
            flexShrink: 0,
            animation: 'pulseGlow 1.5s infinite'
          }} />
          <div style={{
            fontSize: '18px',
            fontWeight: '500',
            color: '#ffffff',
            lineHeight: '1.6',
            flex: 1,
            textAlign: 'left',
            ...(isSearching && {
              background: 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.4) 100%)',
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'textShimmer 3s linear infinite'
            })
          }}>
            {item.text}
          </div>
        </div>
      );
    })}
  </div>
)}

      {/* Farewell */}
      {showFarewell && (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeIn 0.6s ease'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '12px',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
            }}>
              That's a wrap for today! 🎬
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '24px'
            }}>
              Tune in tomorrow for more, {userName}
            </p>
            <button
              onClick={onClose}
              style={{
                padding: '14px 48px',
                borderRadius: '24px',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#000',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>

    {/* News Cards - Fixed at bottom */}
{isPlaying && (newsCards.length > 0 || isLoadingCards) && (
  <div style={{
    position: 'fixed',
    bottom: '110px',
    left: 0,
    right: 0,
    zIndex: 25,
    pointerEvents: 'none',
    paddingTop: '20px'
  }}>
    <div 
      ref={newsCardsRef}
      style={{
        display: 'flex',
        gap: '12px',
        overflowX: 'auto',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '15px',
        paddingBottom: '34px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        scrollBehavior: 'smooth',
        pointerEvents: 'auto'
      }}
      className="news-cards-scroll"
    >
      {isLoadingCards ? (
        // ✅ SHIMMER SKELETON CARDS (show during search)
        [...Array(5)].map((_, idx) => (
          <div
            key={`skeleton-${idx}`}
            style={{
              minWidth: '130px',
              maxWidth: '130px',
              height: '160px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              overflow: 'hidden',
              position: 'relative',
              flexShrink: 0,
              animation: 'fadeInScale 0.4s ease forwards',
              animationDelay: `${idx * 0.08}s`,
              opacity: 0
            }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent)',
              animation: 'shimmer 2s infinite',
              zIndex: 1
            }} />
          </div>
        ))
      ) : (
        // ✅ REAL NEWS CARDS (show after search completes)
        newsCards.map((card, idx) => {
          const isCurrentlyPlaying = idx === currentCardIndex;
          const isPastCard = idx < currentCardIndex;
          
          return (
            <div
              key={card.id}
              style={{
                minWidth: '130px',
                maxWidth: '130px',
                height: '160px',
                borderRadius: '12px',
                background: isCurrentlyPlaying 
                  ? 'rgba(255, 255, 255, 0.18)'
                  : 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: isCurrentlyPlaying 
                  ? `2px solid ${COMPANY_LOGOS[company.ticker]?.color || company.color}` 
                  : '1px solid rgba(255, 255, 255, 0.15)',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: isCurrentlyPlaying ? 1 : (isPastCard ? 0.4 : 0.25),
                transform: isCurrentlyPlaying ? 'scale(1.08)' : 'scale(1)',
                boxShadow: isCurrentlyPlaying 
                  ? `0 12px 32px rgba(0, 0, 0, 0.4), 0 0 0 4px ${COMPANY_LOGOS[company.ticker]?.color || company.color}40` 
                  : '0 4px 16px rgba(0, 0, 0, 0.2)',
                position: 'relative',
                cursor: 'pointer',
                flexShrink: 0,
                filter: isCurrentlyPlaying ? 'brightness(1.15) saturate(1.1)' : 'brightness(0.9)'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleArticleClick(card);
              }}
            >
              {isManualNavigationRef.current && idx === currentCardIndex && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '12px',
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '9px',
                  color: '#fff',
                  fontWeight: '700',
                  letterSpacing: '0.5px',
                  zIndex: 10,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }}>
                  <svg 
                    width="12" 
                    height="12" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M17 11V7C17 4.79086 15.2091 3 13 3H11C8.79086 3 7 4.79086 7 7V11M8 11H16C17.1046 11 18 11.8954 18 13V19C18 20.1046 17.1046 21 16 21H8C6.89543 21 6 20.1046 6 19V13C6 11.8954 6.89543 11 8 11Z" 
                      stroke="#fff" 
                      strokeWidth="2" 
                      strokeLinecap="round"
                    />
                    <circle 
                      cx="12" 
                      cy="16" 
                      r="1.5" 
                      fill="#fff"
                    />
                  </svg>
                  LOCKED
                </div>
              )}
              
              <div style={{
                width: '100%',
                height: '90px',
                overflow: 'hidden',
                position: 'relative',
                borderRadius: '12px 12px 0 0'
              }}>
                <img 
                  src={card.image}
                  alt={card.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.4s ease',
                    transform: isCurrentlyPlaying ? 'scale(1.05)' : 'scale(1)'
                  }}
                />
                
                {isCurrentlyPlaying && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    padding: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#fff',
                      fontSize: '9px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      animation: 'pulseText 1.5s infinite'
                    }}>
                      <Radio size={10} strokeWidth={2.5} />
                      <span>Now Playing</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div style={{
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{
                  fontSize: '8px',
                  fontWeight: '700',
                  color: isCurrentlyPlaying ? `${COMPANY_LOGOS[company.ticker]?.color || company.color}` : 'rgba(147, 197, 253, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  textAlign: 'left'
                }}>
                  {card.source}
                </div>
                
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: isCurrentlyPlaying ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                  lineHeight: '1.3',
                  textAlign: 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {card.title}
                </div>
              </div>
              
              {isCurrentlyPlaying && (
                <div style={{
                  position: 'absolute',
                  top: -3,
                  left: -3,
                  right: -3,
                  bottom: -3,
                  borderRadius: '14px',
                  background: `linear-gradient(135deg, ${COMPANY_LOGOS[company.ticker]?.color || company.color}60, ${COMPANY_LOGOS[company.ticker]?.color || company.color}30, ${COMPANY_LOGOS[company.ticker]?.color || company.color}60)`,
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite',
                  zIndex: -1
                }} />
              )}
            </div>
          );
        })
      )}
    </div>
  </div>
)}

    {/* Controls */}
    <div style={{
      background: 'rgba(244, 251, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      padding: '16px 20px 24px',
      borderTop: '1px solid rgba(255, 255, 255, 0.56)',
      borderRadius: '42px 42px 0 0',
      position: 'relative',
      zIndex: 30
    }}>
      
      <div style={{
        background: '#ffffff',
        borderRadius: '32px',
        padding: '8px 20px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(169, 177, 185, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px'
      }}>
        <button
          onClick={changeSpeed}
          style={{
            padding: '12px 16px',
            borderRadius: '20px',
            background: '#f3f4f6',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: '#000',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
        >
          {playbackSpeed}x
        </button>

<button
  onClick={skipToPreviousCard}
  disabled={currentCardIndex === 0 || !isPlaying || isNavigating}
  style={{
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentCardIndex === 0 || !isPlaying || isNavigating ? '#e5e7eb' : '#f3f4f6',
    border: 'none',
    cursor: currentCardIndex === 0 || !isPlaying || isNavigating ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: currentCardIndex === 0 || !isPlaying || isNavigating ? 0.4 : 1,
    transition: 'all 0.2s'
  }}
  onMouseEnter={(e) => {
    if (currentCardIndex > 0 && isPlaying && !isNavigating) {
      e.currentTarget.style.background = '#e5e7eb';
    }
  }}
  onMouseLeave={(e) => {
    if (currentCardIndex > 0 && isPlaying && !isNavigating) {
      e.currentTarget.style.background = '#f3f4f6';
    }
  }}
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path 
      d="M11 19l-7-7 7-7m8 14l-7-7 7-7" 
      stroke="#000" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
</button>

<button
  onClick={togglePlayPause}
  disabled={isGenerating || isNavigating}
  style={{
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    background: isGenerating || isNavigating ? '#6b7280' : '#000',
    border: 'none',
    cursor: isGenerating || isNavigating ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    opacity: isGenerating || isNavigating ? 0.6 : 1
  }}
  onMouseEnter={(e) => {
    if (!isGenerating && !isNavigating) {
      e.currentTarget.style.transform = 'scale(1.05)';
    }
  }}
  onMouseLeave={(e) => {
    if (!isGenerating && !isNavigating) {
      e.currentTarget.style.transform = 'scale(1)';
    }
  }}
>
  {isPaused || !isPlaying ? (
    <Play size={24} color="white" fill="white" />
  ) : (
    <Pause size={24} color="white" fill="white" />
  )}
</button>
<button
  onClick={skipToNextCard}
  disabled={currentCardIndex >= newsCards.length - 1 || !isPlaying || isNavigating}
  style={{
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentCardIndex >= newsCards.length - 1 || !isPlaying || isNavigating ? '#e5e7eb' : '#f3f4f6',
    border: 'none',
    cursor: currentCardIndex >= newsCards.length - 1 || !isPlaying || isNavigating ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: currentCardIndex >= newsCards.length - 1 || !isPlaying || isNavigating ? 0.4 : 1,
    transition: 'all 0.2s'
  }}
  onMouseEnter={(e) => {
    if (currentCardIndex < newsCards.length - 1 && isPlaying && !isNavigating) {
      e.currentTarget.style.background = '#e5e7eb';
    }
  }}
  onMouseLeave={(e) => {
    if (currentCardIndex < newsCards.length - 1 && isPlaying && !isNavigating) {
      e.currentTarget.style.background = '#f3f4f6';
    }
  }}
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path 
      d="M13 5l7 7-7 7M4 5l7 7-7 7" 
      stroke="#000" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
</button>

      </div>
    </div>

  </div>

  {/* Article Confirmation Modal */}
  {showArticleConfirm && selectedArticle && (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 40,
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '32px',
        maxWidth: '400px',
        margin: '0 20px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        textAlign: 'center'
      }}>
        <div style={{
          width: '100%',
          height: '120px',
          borderRadius: '12px',
          overflow: 'hidden',
          marginBottom: '20px'
        }}>
          <img 
            src={selectedArticle.image}
            alt={selectedArticle.fullTitle}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </div>
        
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#ffffff',
          marginBottom: '8px',
          lineHeight: '1.4'
        }}>
          {selectedArticle.fullTitle}
        </h3>
        
        <p style={{
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.5)',
          marginBottom: '4px'
        }}>
          {selectedArticle.source}
        </p>
        
        <p style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.7)',
          marginBottom: '24px',
          lineHeight: '1.5'
        }}>
          Would you like to read the full article?
        </p>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button
            onClick={cancelArticle}
            style={{
              padding: '12px 24px',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Cancel
          </button>
          
          <button
            onClick={confirmArticle}
            style={{
              padding: '12px 24px',
              borderRadius: '20px',
              background: COMPANY_LOGOS[company.ticker]?.color || company.color,
              border: 'none',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            Read Article
          </button>
        </div>
      </div>
    </div>
  )}
  
  <style>{`
   
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      background-position: 200% 0;
      opacity: 0.6;
    }
  }
  
  @keyframes pulseGlow {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }
  }
  
    @keyframes fadeInScale {
      0% { opacity: 0; transform: scale(0.9); }
      100% { opacity: 1; transform: scale(1); }
    }
    
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.9); }
      to { opacity: 1; transform: scale(1); }
    }
    
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    
    @keyframes pulseText {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.8; }
    }
    
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes fadeInDown {
      from { opacity: 0; transform: translateY(-20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(30px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes blendIn {
      0% { opacity: 0; filter: blur(10px); }
      100% { opacity: 1; filter: blur(0); }
    }

    @keyframes gentleFloat {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-8px); }
    }
    
    @keyframes pulseGlow {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.2); }
    }
    
    @keyframes textShimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    * {
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    button {
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    }

    button:hover {
      transform: translateY(-2px) !important;
    }

    button:active {
      transform: scale(0.98) !important;
    }
    
    .news-cards-scroll::-webkit-scrollbar { display: none; }
    .news-cards-scroll { -ms-overflow-style: none; scrollbar-width: none; }
  `}</style>
</div>
);
};

export default CompaniesLiveStation;